/**
 * Operational email-template definitions (#254).
 *
 * The wording mirrors the source-of-truth Outlook `.msg` templates the operator
 * maintains in Drive. Each template's `build` prefills the field lines from the
 * in-context customer/session (best-effort) and leaves anything unknown blank.
 *
 * Increment 1 covered the two **hold-removal** templates (Drive folder
 * "SCRN 42"), wired into the Account Holds screen (SCRN-42); a later increment
 * adds the **Uniscreens Usage** template (same Drive folder "SCRN 42"),
 * likewise wired into the Account Holds screen. Increment 2 adds
 * the **Proof of Payment** template (Drive folder "SCRN 46 & 47"), wired into
 * the Transaction Information screen (SCRN-47). Increment 3 adds the **New
 * Mobile Number Update** template (Drive folder "SCRN 26"), wired into the
 * Change Mobile Number screen (SCRN-26). Increment 4 adds the **Special
 * Category Data** template (Drive folder "SCRN 13"), wired into the Call
 * History screen (SCRN-13). Increment 5 adds the **SEPA DD Recall** template
 * (Drive folder "SCRN 33 & 34"), wired into the Direct Debits screen (SCRN-33).
 * Increment 6 adds the **Clear Cheque Counter** template (Drive folder
 * "SCRN 41"), wired into the Chequebook screen (SCRN-41). Increment 7 adds the
 * **SEPA CT Recall** template (Drive folder "SCRN 28, 31, 35, 36 & 37"), wired
 * into the Standing Orders screen (SCRN-35/36/37). The Bill Payments surface of
 * SEPA CT Recall (SCRN-28/31) is a follow-up increment. A later increment adds
 * the **Gambling Block** template (Drive folder "SCRN 50"), wired into the Card
 * Maintenance screen (SCRN-49/50). A follow-up increment adds the **Travel
 * Spend** ("Travel Note Request") template (also Drive folder "SCRN 50"),
 * likewise wired into the Card Maintenance screen. A follow-up increment adds
 * the **Apple/Google Pay Token** template (also Drive folder "SCRN 50"),
 * likewise wired into the Card Maintenance screen. A further increment adds the
 * **EDS Data Breach** and **Business Banking Data Breach** templates (also Drive
 * folder "SCRN 50"), likewise wired into the Card Maintenance screen. Further
 * templates are added in follow-up increments, grouped by the screen that
 * carries the context they need.
 */

import type { EmailTemplate, TemplateContext } from './mailto.js';
import { fullCard } from './mailto.js';
import { escapeHtml } from './eml.js';

/**
 * "R hold removal" — request Current Accounts to remove a Cannot-create-DD
 * (R) hold after the agent has reviewed 3 months of account activity.
 * Source: Drive "SCRN 42 / R hold removal.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` — the plain-text `build` is retained for the subject and as a
 * reference for the wording. Same best-effort prefill (`customerName`,
 * `accountNumber`) with the `(Customer Name)` title fallback; interpolated
 * context values are HTML-escaped.
 */
export const R_HOLD_REMOVAL: EmailTemplate = {
  id: 'r-hold-removal',
  label: 'R Hold Removal',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: '**YOUR TEAM LEADER**',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', accountNumber = '', content }) => {
    // #637 inc 14 — the subject/title STEMS, the two instruction sentences and
    // the field LABELS are admin-editable via the content catalogue; the default
    // passed here is byte-identical to the previous compiled-in wording, so with
    // no override the output is unchanged. The auto-filled values (customerName /
    // accountNumber) + the `- ${name}` / `(Customer Name)` title suffix stay
    // fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: `${line('email.hold_removal_r.subject', 'R hold removal draft')}${customerName ? ` - ${customerName}` : ''}`,
      body: [
        `${line('email.hold_removal_r.title', 'R hold Removal')} - ${customerName || '(Customer Name)'}`,
        '',
        line(
          'email.hold_removal_r.instruction_reviewed',
          'Please can you remove the R hold on the following account, I have fully reviewed the account for the last 3 months',
        ),
        line(
          'email.hold_removal_r.instruction_no_returns',
          'I can confirm the customer has no returned Direct Debits or cheques in the last 3 months',
        ),
        '',
        `${line('email.hold_removal_r.label_customer_name', 'Customer Name:')} ${customerName}`,
        '',
        `${line('email.hold_removal_r.label_account_number', 'Account Number:')} ${accountNumber}`,
        '',
      ].join('\n'),
    };
  },
  // buildHtml reproduces the exact source formatting of the provided Outlook
  // template ("SCRN 42 / R hold removal.msg", #434 08:48 requirement — the
  // templates must match the originals). The source is HTML-encapsulated RTF: a
  // bold+underlined 16pt "Inter" title, two **bold, PTSB-orange (#FC4C02)** 12pt
  // instruction lines, and a bordered two-column table (label | value) for
  // Customer Name / Account Number. Interpolated context values are HTML-escaped.
  buildHtml: ({ customerName = '', accountNumber = '', content }) => {
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    // #637 inc 14 — resolve the editable wording; the default is byte-identical
    // to the previous compiled-in text. Escape the resolved value so an admin
    // reword can never inject markup into the exact-match Outlook HTML (the fixed
    // layout/table/fonts/colours + the `- (Customer Name)` title suffix stay in
    // code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: \'Inter\', serif; font-size: 12pt; color: #000000;">',
      `<p style="text-align: justify;"><b><u><span style="font-size: 16pt; font-family: 'Inter', serif;">${line('email.hold_removal_r.title', 'R hold Removal')} - ${name || '(Customer Name)'}</span></u></b></p>`,
      '<p style="margin-left: 18pt;">&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.hold_removal_r.instruction_reviewed', 'Please can you remove the R hold on the following account, I have fully reviewed the account for the last 3 months')}</span></b></p>`,
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.hold_removal_r.instruction_no_returns', 'I can confirm the customer has no returned Direct Debits or cheques in the last 3 months')}</span></b></p>`,
      '<p>&nbsp;</p>',
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      '<tr>',
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${line('email.hold_removal_r.label_customer_name', 'Customer Name:')}</span></b></p></td>`,
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-left: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${name}</span></b></p></td>`,
      '</tr>',
      '<tr>',
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-top: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${line('email.hold_removal_r.label_account_number', 'Account Number:')}</span></b></p></td>`,
      `<td valign="top" style="width: 207.4pt; border-top: none; border-left: none; border-bottom: solid #000000 1pt; border-right: solid #000000 1pt; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${account}</span></b></p></td>`,
      '</tr>',
      '</table>',
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "N hold removal" — request removal of a Pay-No-Withdrawal (N) hold once the
 * customer has paid the outstanding balance in full.
 * Source: Drive "SCRN 42 / N Hold removal.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` — the plain-text `build` is retained for the subject and as a
 * reference for the wording. Same best-effort prefill (`accountNumber`);
 * interpolated context values are HTML-escaped.
 */
export const N_HOLD_REMOVAL: EmailTemplate = {
  id: 'n-hold-removal',
  label: 'N Hold Removal',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'Unsecuredmailbox@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ accountNumber = '', content }) => {
    // #637 inc 14 — the subject, title, the "Account Number:" label, the
    // paid-in-full sentence and the closing line are admin-editable via the
    // content catalogue; the default passed here is byte-identical to the
    // previous compiled-in wording, so with no override the output is unchanged.
    // The auto-filled account number stays fixed in code (text only, never
    // logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line(
        'email.hold_removal_n.subject',
        'Request for N Hold Removal - Template',
      ),
      body: [
        line('email.hold_removal_n.title', 'Request for N Hold removal:'),
        '',
        `${line('email.hold_removal_n.label_account_number', 'Account Number:')} ${accountNumber}`,
        '',
        line(
          'email.hold_removal_n.paid_in_full',
          'There is an N Hold on the above account and customer has paid the outstanding balance in full.',
        ),
        '',
        line(
          'email.hold_removal_n.closing',
          'Can this N Hold please be removed?',
        ),
        '',
      ].join('\n'),
    };
  },
  // buildHtml reproduces the exact source formatting of the provided Outlook
  // template ("SCRN 42 / N Hold removal.msg", #434 08:48 requirement — the
  // templates must match the originals). Like its R-hold sibling the source is
  // HTML-encapsulated RTF in the "Inter" font: a bold+underlined 16pt title, a
  // bold 12pt "Account Number:" label on its own line (the account value on the
  // next line), the plain 12pt paid-in-full sentence, and a bold, PTSB-orange
  // (#FC4C02) 12pt "Can this N Hold please be removed?" closing line.
  // Interpolated context values are HTML-escaped.
  buildHtml: ({ accountNumber = '', content }) => {
    const account = escapeHtml(accountNumber);
    // #637 inc 14 — resolve the editable wording; the default is byte-identical
    // to the previous compiled-in text. Escape the resolved value so an admin
    // reword can never inject markup into the exact-match Outlook HTML (the fixed
    // layout/fonts/colours stay in code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: \'Inter\', serif; font-size: 12pt; color: #000000;">',
      `<p><b><u><span style="font-size: 16pt; font-family: 'Inter', serif;">${line('email.hold_removal_n.title', 'Request for N Hold removal:')}</span></u></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.hold_removal_n.label_account_number', 'Account Number:')}</span></b></p>`,
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${account}</span></b></p>`,
      `<p><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.hold_removal_n.paid_in_full', 'There is an N Hold on the above account and customer has paid the outstanding balance in full.')}</span></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.hold_removal_n.closing', 'Can this N Hold please be removed?')}</span></b></p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "Uniscreens Usage" — the record the agent sends to the Open24 Quality
 * Assurance mailbox whenever an action is processed through Uniscreens for a
 * Business customer or a Non-registered/Registered/Exempt Personal customer,
 * so the usage is reported to QA. The draft carries the QA reporting note and
 * both the Business and Personal field blocks (secure-identification, action
 * completed, reason for Uniscreens usage incl. why GTX was not used, and the
 * Team Leader approval line) verbatim from the source template. Prefilled
 * best-effort from the Account Holds screen's in-context detail: the customer
 * name and the account number / CIF are threaded into the subject in place of
 * the source template's `**Account Number/CIF**` / `**Customer Name**`
 * placeholders (the account number is preferred, falling back to the CIF);
 * every Y/N answer, the action detail, the reason and the Team-Leader approval
 * are left blank for the agent to complete on the call. Targets the Open24
 * Quality Assurance mailbox.
 * Source: Drive "SCRN 42 / Uniscreens Usage.msg".
 *
 * `buildHtml` reproduces the exact source formatting of the provided Outlook
 * template ("SCRN 42 / Uniscreens Usage.msg", #434 08:48 requirement — the
 * templates must match the originals). Like its R/N-hold siblings the source is
 * HTML-encapsulated RTF in the "Inter" font with everything bold: a
 * bold+underlined 16pt title, a bold PTSB-orange (#FC4C02) QA-reporting note,
 * bold+underlined 12pt PTSB-orange `Business` / `Personal` section headers, the
 * bold 12pt black field labels with the two `Y/N` fragments in PTSB-orange, a
 * bold PTSB-orange `Approved by (Team Leader name required):` line, and a
 * bold+italic black closing "TL approval not required…" line, with blank spacer
 * paragraphs between items. There are no interpolated fields in the body, so it
 * stays static; the plain-text `build` is retained for the subject/recipient and
 * as a reference for the wording. Every Y/N answer, action detail, reason and the
 * Team-Leader approval stay blank for the agent to complete on the call.
 */
export const UNISCREENS_USAGE: EmailTemplate = {
  id: 'uniscreens-usage',
  label: 'Uniscreens Usage',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'Open24QualityAssurance@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', accountNumber = '', cif = '', content }) => {
    // #637 inc 15 — the subject/title STEM, the QA-reporting note, the
    // Business/Personal section headers and the field LABELS (identified / Y/N /
    // action / reason / approved-by / closing) are admin-editable via the content
    // catalogue; the default passed here is byte-identical to the previous
    // compiled-in wording, so with no override the output is unchanged. The
    // auto-filled subject values (account number / CIF / customer name), their
    // `**…**` fallbacks and the `-  ` / ` - ` separators stay fixed in code
    // (text only, never logic). The "Reason…" label keeps its trailing space as a
    // literal outside the key so output stays byte-identical.
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: `${line('email.uniscreens.subject_stem', 'Uniscreens Usage')} - ${
        accountNumber || cif || '**Account Number/CIF**'
      } - ${customerName || '**Customer Name**'}`,
      body: [
        line('email.uniscreens.title', 'Uniscreens Usage'),
        line(
          'email.uniscreens.qa_note',
          'Any actions processed through Uniscreens for Business customers or Non-registered/Registered/Exempt Personal Customers must be reported to QA Mailbox',
        ),
        ' ',
        line('email.uniscreens.header_business', 'Business'),
        ' ',
        `${line('email.uniscreens.label_identified', 'Was customer securely Identified?')} ${line('email.uniscreens.yn', 'Y/N')}`,
        ' ',
        line('email.uniscreens.label_action', 'Action completed:'),
        ' ',
        `${line('email.uniscreens.label_reason', 'Reason for Uniscreens Usage (must include why GTX was not used if option was available):')} `,
        ' ',
        line('email.uniscreens.header_personal', 'Personal'),
        ' ',
        `${line('email.uniscreens.label_identified', 'Was customer securely Identified?')} ${line('email.uniscreens.yn', 'Y/N')}`,
        ' ',
        line('email.uniscreens.label_action', 'Action completed:'),
        ' ',
        `${line('email.uniscreens.label_reason', 'Reason for Uniscreens Usage (must include why GTX was not used if option was available):')} `,
        ' ',
        line(
          'email.uniscreens.label_approved',
          'Approved by (Team Leader name required):',
        ),
        ' ',
        line(
          'email.uniscreens.closing',
          'TL approval not required for waiving Visa Debit Card replacement fee if customer has insufficient funds or if previous card was not received and is still listed as inactive',
        ),
      ].join('\n'),
    };
  },
  // buildHtml reproduces the exact source formatting of the provided Outlook
  // template ("SCRN 42 / Uniscreens Usage.msg", #434 08:48 requirement — the
  // templates must match the originals). HTML-encapsulated RTF in the "Inter"
  // font, everything bold: bold+underlined 16pt black title; bold PTSB-orange
  // (#FC4C02) QA note; bold+underlined 12pt PTSB-orange "Business"/"Personal"
  // section headers; bold 12pt black field labels with the two "Y/N" fragments
  // in PTSB-orange; a bold PTSB-orange "Approved by…" line; and a bold+italic
  // black closing "TL approval not required…" line, with blank spacer
  // paragraphs between items. No interpolated context fields in the body.
  buildHtml: ({ content }) => {
    // #637 inc 15 — resolve the editable wording; the default is byte-identical
    // to the previous compiled-in text. Escape the resolved value so an admin
    // reword can never inject markup into the exact-match Outlook HTML (the fixed
    // layout/fonts/colours + the trailing spaces inside each span stay in code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: \'Inter\', serif; font-size: 12pt; color: #000000;">',
      `<p><b><u><span style="font-size: 16pt; font-family: 'Inter', serif;">${line('email.uniscreens.title', 'Uniscreens Usage')}</span></u></b></p>`,
      `<p><b><span style="font-family: 'Inter', serif; color: #FC4C02;">${line('email.uniscreens.qa_note', 'Any actions processed through Uniscreens for Business customers or Non-registered/Registered/Exempt Personal Customers must be reported to QA Mailbox')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><u><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.uniscreens.header_business', 'Business')}</span></u></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.label_identified', 'Was customer securely Identified?')} </span><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.uniscreens.yn', 'Y/N')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.label_action', 'Action completed:')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.label_reason', 'Reason for Uniscreens Usage (must include why GTX was not used if option was available):')} </span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><u><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.uniscreens.header_personal', 'Personal')}</span></u></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.label_identified', 'Was customer securely Identified?')} </span><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.uniscreens.yn', 'Y/N')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.label_action', 'Action completed:')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.label_reason', 'Reason for Uniscreens Usage (must include why GTX was not used if option was available):')} </span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.uniscreens.label_approved', 'Approved by (Team Leader name required):')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><i><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.uniscreens.closing', 'TL approval not required for waiving Visa Debit Card replacement fee if customer has insufficient funds or if previous card was not received and is still listed as inactive')}</span></i></b></p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/** Templates surfaced on the Account Holds screen (SCRN-42). */
export const ACCOUNT_HOLDS_TEMPLATES: readonly EmailTemplate[] = [
  R_HOLD_REMOVAL,
  N_HOLD_REMOVAL,
  UNISCREENS_USAGE,
];

/**
 * "Proof of Payment" — request the SEPA queries team to issue a proof-of-payment
 * to a company (not the customer). Prefilled from the Transaction Information
 * screen's in-context detail (customer name, account number, SEPA destination
 * name, posting date, reference, amount); the company recipient details are
 * unknown to the app and left blank for the agent to complete.
 * Source: Drive "SCRN 46 & 47 / PROOF OF PAYMENT.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` — the plain-text `build` is retained for the subject/recipient
 * and as a reference for the wording. The `PROOF OF PAYMENT` title is rendered
 * bold and the labelled fields carry bold labels; the same best-effort prefill
 * is used with interpolated context values HTML-escaped, and the company
 * details / screenshot instruction stay blank for the agent to complete.
 */
export const PROOF_OF_PAYMENT: EmailTemplate = {
  id: 'proof-of-payment',
  label: 'Proof of Payment',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'sepa.queries@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({
    customerName = '',
    accountNumber = '',
    destinationName = '',
    postingDate = '',
    transactionReference = '',
    transactionAmount = '',
    content,
  }) => {
    // #637 inc 16 — the title (which is also the subject), the two
    // company-only/include-all warning lines, the nine field labels and the
    // screenshot instruction are admin-editable via the content catalogue; the
    // default passed here is byte-identical to the previous compiled-in wording,
    // so with no override the output is unchanged. The interpolated context
    // values (customer name / account / destination / posting date / reference /
    // amount), the `Label: ${value}` structure and the blank spacer lines stay
    // fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line('email.proof_of_payment.title', 'PROOF OF PAYMENT'),
      body: [
        line('email.proof_of_payment.title', 'PROOF OF PAYMENT'),
        line(
          'email.proof_of_payment.warning_company_only',
          'Please note we can only send a proof of payment to a company and not the customer',
        ),
        line(
          'email.proof_of_payment.warning_include_all',
          'Please include all below information or the draft will be sent back to you and this will cause a delay for the customer',
        ),
        '',
        `${line('email.proof_of_payment.label_customer_name', 'Customer Name:')} ${customerName}`,
        '',
        `${line('email.proof_of_payment.label_account_number', 'Account Number:')} ${accountNumber}`,
        '',
        `${line('email.proof_of_payment.label_destination_name', 'Destination Name:')} ${destinationName}`,
        '',
        `${line('email.proof_of_payment.label_posting_date', 'Posting Date of Transaction:')} ${postingDate}`,
        '',
        `${line('email.proof_of_payment.label_reference', 'Reference of Transaction:')} ${transactionReference}`,
        '',
        `${line('email.proof_of_payment.label_amount', 'Amount of Transaction:')} ${transactionAmount}`,
        '',
        line('email.proof_of_payment.label_company_name', 'Company Name:'),
        '',
        line(
          'email.proof_of_payment.label_company_email',
          'Email Address for Company:',
        ),
        '',
        line(
          'email.proof_of_payment.label_company_contact',
          'Contact Name for Company:',
        ),
        '',
        line(
          'email.proof_of_payment.screenshot_instruction',
          'Insert screenshot of the transaction from the 27 screen here',
        ),
        '',
      ].join('\n'),
    };
  },
  // buildHtml reproduces the exact source formatting of the provided Outlook
  // template ("SCRN 46 & 47 / PROOF OF PAYMENT.msg", #434 08:48 requirement).
  // The source is Word-generated HTML-encapsulated RTF: a bold+underlined 16pt
  // "Inter" title (black), two bold PTSB-orange (#FC4C02) 12pt warning lines,
  // a nine-row two-column bordered table (207.4pt wide columns, solid 1pt
  // border) with bold "Inter" labels in the left column and prefilled values in
  // the right, and a bold PTSB-orange 12pt screenshot instruction at the foot.
  // Interpolated context values are HTML-escaped.
  buildHtml: ({
    customerName = '',
    accountNumber = '',
    destinationName = '',
    postingDate = '',
    transactionReference = '',
    transactionAmount = '',
    content,
  }) => {
    // #637 inc 16 — resolve the editable wording (title, warnings, labels,
    // screenshot instruction); the default is byte-identical to the previous
    // compiled-in text. Escape the resolved value so an admin reword can never
    // inject markup into the exact-match Outlook HTML (the fixed table
    // layout/fonts/colours/borders + the interpolated context values stay in
    // code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    const destination = escapeHtml(destinationName);
    const posting = escapeHtml(postingDate);
    const reference = escapeHtml(transactionReference);
    const amount = escapeHtml(transactionAmount);
    const labelCell = (text: string) =>
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-top: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${text}</span></b></p></td>`;
    const valueCell = (val: string, lastCol = false) =>
      lastCol
        ? `<td valign="top" style="width: 207.4pt; border-top: none; border-left: none; border-bottom: solid #000000 1pt; border-right: solid #000000 1pt; padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-family: 'Inter', serif;">${val}</span></p></td>`
        : `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-top: none; border-left: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-family: 'Inter', serif;">${val}</span></p></td>`;
    const row = (label: string, val: string, last = false) =>
      `<tr>${labelCell(label)}${valueCell(val, last)}</tr>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      `<body style="font-family: 'Inter', serif; font-size: 12pt; color: #000000;">`,
      `<p style="text-align: justify;"><b><u><span style="font-size: 16pt; font-family: 'Inter', serif;">${line('email.proof_of_payment.title', 'PROOF OF PAYMENT')}</span></u></b></p>`,
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.proof_of_payment.warning_company_only', 'Please note we can only send a proof of payment to a company and not the customer')}</span></b></p>`,
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.proof_of_payment.warning_include_all', 'Please include all below information or the draft will be sent back to you and this will cause a delay for the customer')}</span></b></p>`,
      '<p>&nbsp;</p>',
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      // First row has full top border
      `<tr><td valign="top" style="width: 207.4pt; border: solid #000000 1pt; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${line('email.proof_of_payment.label_customer_name', 'Customer Name:')}</span></b></p></td><td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-left: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-family: 'Inter', serif;">${name}</span></p></td></tr>`,
      row(
        line('email.proof_of_payment.label_account_number', 'Account Number:'),
        account,
      ),
      row(
        line(
          'email.proof_of_payment.label_destination_name',
          'Destination Name:',
        ),
        destination,
      ),
      row(
        line(
          'email.proof_of_payment.label_posting_date',
          'Posting Date of Transaction:',
        ),
        posting,
      ),
      row(
        line(
          'email.proof_of_payment.label_reference',
          'Reference of Transaction:',
        ),
        reference,
      ),
      row(
        line('email.proof_of_payment.label_amount', 'Amount of Transaction:'),
        amount,
      ),
      row(
        line('email.proof_of_payment.label_company_name', 'Company Name:'),
        '',
      ),
      row(
        line(
          'email.proof_of_payment.label_company_email',
          'Email Address for Company:',
        ),
        '',
      ),
      row(
        line(
          'email.proof_of_payment.label_company_contact',
          'Contact Name for Company:',
        ),
        '',
        true,
      ),
      '</table>',
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.proof_of_payment.screenshot_instruction', 'Insert screenshot of the transaction from the 27 screen here')}</span></b></p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * Templates surfaced on the Transaction Information screen (SCRN-47). #771
 * (@dylan219) adds Missing Payments + SEPA Compliance alongside Proof of
 * Payment. The two new templates are declared later in this file (they group
 * with the SEPA CT payment-screen templates), so this array is assembled below
 * where all three ids are in scope ({@link TRANSACTION_INFORMATION_TEMPLATES}).
 */

/**
 * "Request to Remove Merchant Block" — request Card Operations (Disputes) to
 * remove a merchant block that has been placed on a customer's card/account.
 * Prefilled best-effort from the Transactions screen's in-context detail
 * (customer name and the selected account number); the card number, merchant
 * name, block date and the Team-Leader-Cc'd answer are left blank / with the
 * source template's own guidance for the agent to complete on the call. The
 * card-number field is deliberately NOT prefilled — a full PAN must never be
 * rendered in an email body (PCI), so the agent enters it manually per the
 * source template's 16-digit instruction. Targets the Card Operations Disputes
 * mailbox.
 * Source: Drive "SCRN 46 & 47 / REQUEST TO REMOVE MERCHANT BLOCK.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` — the plain-text `build` is retained for the subject/recipient
 * and as a mailto fallback for the wording. `buildHtml` reproduces the exact
 * source formatting of the provided Outlook template ("SCRN 46 & 47 / REQUEST TO
 * REMOVE MERCHANT BLOCK.msg", #434 08:48 requirement — the templates must match
 * the originals). Unlike the hold-removal siblings this source is a Word-authored
 * message in **Calibri** (not "Inter"), everything bold: a centred 18pt orange
 * (`#E46C0A`) two-line heading — "Once completed <u>in full</u>" then "Send to
 * the CardOpsDisputes@ptsb.ie mailbox" (the address a `#FF6600` mailto
 * link) — a 12pt blue (`#1F497D`) SLA line, then the 12pt blue-grey (`#44546A`)
 * field labels with the "(NN digit format…)" / "(DD/MM/YYYY format):" fragments
 * in **red**, the "(copy details from TSYS / Unibanks)" fragment in italics, the
 * "Y/N" Team-Leader line, and a red "(If your TL is absent…)" line, with blank
 * spacer paragraphs between items. The same best-effort prefill (`customerName`,
 * `accountNumber`) is used with interpolated context values HTML-escaped. The
 * card-number field stays blank in the HTML too (PCI — never render a PAN), and
 * the merchant name, block date and the Team-Leader Cc'd answer stay blank for
 * the agent to complete.
 */
export const REMOVE_MERCHANT_BLOCK: EmailTemplate = {
  id: 'remove-merchant-block',
  label: 'Add/Remove Merchant Block',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'CardOpsDisputes@ptsb.ie, DDVisaDisputes@ptsb.ie',
  cc: '**YOUR TEAM LEADER**',
  build: ({ customerName = '', accountNumber = '', content }) => {
    // #637 inc 17 — the subject, the two-line "Once completed…/Send to…mailbox"
    // heading, the SLA line, the field LABELS (with their "(NN digit format…)" /
    // "(DD/MM/YYYY format):" fragments and the "(copy details from TSYS /
    // Unibanks)" fragment) and the Team-Leader Y/N line + "(If your TL is
    // absent…)" note are admin-editable via the content catalogue; the default
    // passed here is byte-identical to the previous compiled-in wording, so with
    // no override the output is unchanged. The auto-filled values (customerName /
    // accountNumber), the `Label: ${value}` structure, the blank spacer lines and
    // the deliberately-blank card-number line (PCI) stay fixed in code (text
    // only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line(
        'email.remove_merchant_block.subject',
        'REQUEST TO REMOVE MERCHANT BLOCK',
      ),
      body: [
        // The heading "in full" word is underlined in the HTML path, so its
        // prefix + emphasised word are separate editable fragments (the same
        // keys the HTML path uses); the plain text joins them with a space.
        `${line('email.remove_merchant_block.heading_completed_prefix', 'Once completed')} ${line('email.remove_merchant_block.heading_completed_emphasis', 'in full')}`,
        // The mailbox address stays fixed in code (a routing address / mailto
        // link in the HTML path), so the editable heading is the surrounding
        // prefix/suffix wording either side of it.
        `${line('email.remove_merchant_block.heading_send_prefix', 'Send to the')} CardOpsDisputes@ptsb.ie ${line('email.remove_merchant_block.heading_send_suffix', 'mailbox')}`,
        ' ',
        ' ',
        line(
          'email.remove_merchant_block.sla',
          '*The SLA for Card Operations to action email requests is 48 hours',
        ),
        ' ',
        `${line('email.remove_merchant_block.label_customer_name', 'Customer Name:')} ${customerName}`,
        ' ',
        // Card number is NEVER prefilled — a full PAN must not appear in a body
        // (PCI). The grey label stem + the red "(NN digit format…)" fragment are
        // the same editable keys the HTML path uses. The trailing space after the
        // format fragment is preserved from the source template.
        `${line('email.remove_merchant_block.label_card_number', 'Visa Debit/Credit Card Number')} ${line('email.remove_merchant_block.label_card_number_format', '(16 digit format, no spaces, no special characters):')} `,
        ' ',
        `${line('email.remove_merchant_block.label_account_number', 'Current Account Number (Debit Cards only)')} ${line('email.remove_merchant_block.label_account_number_format', '(14 digit format, no spaces, no special characters):')}${
          accountNumber ? ` ${accountNumber}` : ''
        }`,
        ' ',
        `${line('email.remove_merchant_block.label_merchant_name', 'Merchant Name')} (${line('email.remove_merchant_block.label_merchant_name_source', 'copy details from TSYS / Unibanks')}):`,
        ' ',
        `${line('email.remove_merchant_block.label_block_date', 'Date Block was placed on account')} ${line('email.remove_merchant_block.label_block_date_format', '(DD/MM/YYYY format):')}`,
        ' ',
        line(
          'email.remove_merchant_block.label_team_leader_cc',
          'Has your Team Leader been Cc’d on this email: Y/N',
        ),
        line(
          'email.remove_merchant_block.note_tl_absent',
          '(If your TL is absent please Cc another TL)',
        ),
        ' ',
        '',
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', accountNumber = '', content }) => {
    // #637 inc 17 — resolve the editable wording (heading, SLA, labels + their
    // red format/DD-MM-YYYY fragments, the italic TSYS/Unibanks fragment, the
    // Team-Leader Y/N line and the "(If your TL is absent…)" note); the default
    // is byte-identical to the previous compiled-in text. Escape the resolved
    // value so an admin reword can never inject markup into the exact-match
    // Outlook HTML (the fixed Calibri layout/colours + the interpolated context
    // values + the mailto link stay fixed in code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    // Colours + font are lifted verbatim from the source .msg (Word/Calibri):
    // orange #E46C0A heading, #FF6600 mailto link, blue #1F497D SLA line, the
    // field labels in blue-grey #44546A with their "(NN digit format…)" /
    // "(DD/MM/YYYY format):" fragments in red.
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: \'Calibri\', sans-serif; font-size: 12pt; color: #44546A;">',
      // The heading is split into an "Once completed" prefix + the underlined
      // "in full" word (the <u> markup is fixed formatting, not editable text).
      `<p style="text-align: center;"><b><span style="font-size: 18pt; font-family: 'Calibri', sans-serif; color: #E46C0A;">${line('email.remove_merchant_block.heading_completed_prefix', 'Once completed')} <u>${line('email.remove_merchant_block.heading_completed_emphasis', 'in full')}</u></span></b></p>`,
      // The mailbox address is a fixed mailto link; the editable heading is the
      // "Send to the" prefix + "mailbox" suffix either side of it.
      `<p style="text-align: center;"><b><span style="font-size: 18pt; font-family: 'Calibri', sans-serif; color: #E46C0A;">${line('email.remove_merchant_block.heading_send_prefix', 'Send to the')} </span></b><span style="font-size: 18pt; font-family: 'Calibri', sans-serif;"><a href="mailto:CardOpsDisputes@ptsb.ie" style="color: #FF6600;">CardOpsDisputes@ptsb.ie</a> </span><b><span style="font-size: 18pt; font-family: 'Calibri', sans-serif; color: #E46C0A;">${line('email.remove_merchant_block.heading_send_suffix', 'mailbox')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-family: 'Calibri', sans-serif; color: #1F497D;">${line('email.remove_merchant_block.sla', '*The SLA for Card Operations to action email requests is 48 hours')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: #44546A;">${line('email.remove_merchant_block.label_customer_name', 'Customer Name:')} ${name}</span></b></p>`,
      '<p>&nbsp;</p>',
      // Card number is NEVER prefilled — a full PAN must not appear in a body (PCI).
      // The grey label stem and the red format fragment are separate spans.
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: #44546A;">${line('email.remove_merchant_block.label_card_number', 'Visa Debit/Credit Card Number')} </span><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: red;">${line('email.remove_merchant_block.label_card_number_format', '(16 digit format, no spaces, no special characters):')} </span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: #44546A;">${line('email.remove_merchant_block.label_account_number', 'Current Account Number (Debit Cards only)')} </span><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: red;">${line('email.remove_merchant_block.label_account_number_format', '(14 digit format, no spaces, no special characters):')}${
        account ? ` ${account}` : ''
      }</span></b></p>`,
      '<p>&nbsp;</p>',
      // The label stem + the italic "(copy details from TSYS / Unibanks)"
      // fragment are separate editable text runs; the <i> markup stays fixed.
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: #44546A;">${line('email.remove_merchant_block.label_merchant_name', 'Merchant Name')} (<i>${line('email.remove_merchant_block.label_merchant_name_source', 'copy details from TSYS / Unibanks')}</i>):</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: #44546A;">${line('email.remove_merchant_block.label_block_date', 'Date Block was placed on account')} </span><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: red;">${line('email.remove_merchant_block.label_block_date_format', '(DD/MM/YYYY format):')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: #44546A;">${line('email.remove_merchant_block.label_team_leader_cc', 'Has your Team Leader been Cc’d on this email: Y/N')}</span></b></p>`,
      `<p><b><span style="font-size: 12pt; font-family: 'Calibri', sans-serif; color: red;">${line('email.remove_merchant_block.note_tl_absent', '(If your TL is absent please Cc another TL)')}</span></b></p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "New Mobile Number Update" — the request the agent sends to their Team Lead
 * (CC'd per the operator's process) to have a customer's mobile number changed
 * on Unibanks and TSYS. Mobile numbers are never changed on the call: the agent
 * completes this template and it is actioned within 24 hours (CCF-6). Prefilled
 * from the Change-Mobile-Number screen's in-context detail (customer name, CIF,
 * and the new mobile number the agent captured); the Card Number(s) line (#866 —
 * the VISA debit card, which the in-context Open24 card is not), the "Name of
 * person who listened to call" and the change reason are left blank for the
 * agent to complete.
 * Source: Drive "SCRN 26 / New Mobile update change template.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) whose
 * `buildHtml` reproduces the source `.msg` look-and-feel exactly (see the
 * `buildHtml` comment below); the plain-text `build` carries the same content
 * for the subject and as a mailto fallback. Best-effort prefill (`customerName`,
 * `cif`, `newMobileNumber`) is interpolated with values HTML-escaped; the Card
 * Number(s) (#866), "Name of person who listened to call" and change-reason
 * lines stay blank for the agent.
 */
export const NEW_MOBILE_NUMBER_UPDATE: EmailTemplate = {
  id: 'new-mobile-number-update',
  label: 'Mobile Number Update',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: '**YOUR TEAM LEADER**',
  cc: 'Aaron.Kearney@ptsb.ie',
  build: ({ customerName = '', cif = '', newMobileNumber = '', content }) => {
    // #637 inc 13 — the fixed instruction/advisory sentences, subject and field
    // LABELS are admin-editable via the content catalogue; the default passed
    // here is byte-identical to the previous compiled-in wording, so with no
    // override the output is unchanged. The auto-filled values + agent-blank
    // lines stay fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line(
        'email.mobile_update.subject',
        'New Mobile update change template',
      ),
      body: [
        line(
          'email.mobile_update.instruction_do_not_process',
          'Do not process any mobile number update on any system.',
        ),
        line(
          'email.mobile_update.instruction_send_team_lead',
          'Complete the below request and send to your Team Lead and CC Aaron Kearney',
        ),
        line(
          'email.mobile_update.instruction_weekend',
          'Weekend updates to be sent to Niamh Vaughan with Team Lead CC’d.',
        ),
        '',
        line(
          'email.mobile_update.customer_advisory',
          'Customer should be informed that we do not change mobile numbers on the call, and that the request will be sent to the relevant Department for processing and this could take 24 hours.',
        ),
        '',
        line(
          'email.mobile_update.turnaround_note',
          'Mobile numbers will be updated within 24 hours for the customer.',
        ),
        '',
        line(
          'email.mobile_update.update_instruction',
          'Please update the mobile number on Unibanks and TSYS for the following Customer(s)',
        ),
        '',
        `${line('email.mobile_update.label_customer_name', 'Customer Name:')} ${customerName}`,
        '',
        `${line('email.mobile_update.label_customer_cif', 'Customer CIF:')} ${cif}`,
        '',
        // #866 — @dylan219: the Card Number(s) line is left for the agent to
        // complete (the in-context card is the Open24 card, not the VISA debit
        // card this field asks for), like the other agent-filled lines.
        line('email.mobile_update.label_card_number', 'Card Number(s):'),
        '',
        `${line('email.mobile_update.label_new_mobile_number', 'New Mobile Number:')} ${newMobileNumber}`,
        '',
        line(
          'email.mobile_update.label_listened',
          'Name of person who listened to call:',
        ),
        '',
        line(
          'email.mobile_update.label_reason',
          'Reason for the change of mobile number:',
        ),
        '',
      ].join('\n'),
    };
  },
  // buildHtml reproduces the exact source formatting of the provided Outlook
  // template ("SCRN 26 / New Mobile update change template.msg", #434 — the
  // templates must match the originals). The source is Word-authored
  // HTML-encapsulated markup in the "Inter" font: three centred, bold+underlined
  // instruction lines (do-not-process / send-to-Team-Lead+CC / weekend updates),
  // a centred italic customer-advisory line, a bold PTSB-orange (#FC4C02) 12pt
  // "updated within 24 hours" line, the plain justified "Please update…" intro,
  // and a bordered two-column (label | value) table for Customer Name / Customer
  // CIF / Card Number(s) / New Mobile Number / Name of person who listened to
  // call / Reason for the change of mobile number. Card Number(s) is left blank
  // for the agent (#866). Interpolated context values are HTML-escaped.
  buildHtml: ({
    customerName = '',
    cif = '',
    newMobileNumber = '',
    content,
  }) => {
    const name = escapeHtml(customerName);
    const customerCif = escapeHtml(cif);
    const mobile = escapeHtml(newMobileNumber);
    // #637 inc 13 — resolve the editable wording; the default is byte-identical
    // to the previous compiled-in text. Escape the resolved value so an admin
    // reword can never inject markup into the exact-match Outlook HTML (the
    // fixed layout/table/fonts/colours stay in code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const labelCell = (label: string, first: boolean) =>
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt;${first ? '' : ' border-top: none;'} padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${label}</span></b></p></td>`;
    const valueCell = (value: string, first: boolean) =>
      `<td valign="top" style="width: 207.4pt;${first ? ' border: solid #000000 1pt; border-left: none;' : ' border-top: none; border-left: none; border-bottom: solid #000000 1pt; border-right: solid #000000 1pt;'} padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-family: 'Inter', serif;">${value || '&nbsp;'}</span></p></td>`;
    const row = (label: string, value: string, first: boolean) =>
      `<tr>${labelCell(label, first)}${valueCell(value, first)}</tr>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: \'Inter\', serif; font-size: 12pt; color: #000000;">',
      `<p style="text-align: center;"><b><u><span style="font-family: 'Inter', serif;">${line('email.mobile_update.instruction_do_not_process', 'Do not process any mobile number update on any system.')}</span></u></b></p>`,
      `<p style="text-align: center;"><b><u><span style="font-family: 'Inter', serif;">${line('email.mobile_update.instruction_send_team_lead', 'Complete the below request and send to your Team Lead and CC Aaron Kearney')}</span></u></b></p>`,
      `<p style="text-align: center;"><b><u><span style="font-family: 'Inter', serif;">${line('email.mobile_update.instruction_weekend', 'Weekend updates to be sent to Niamh Vaughan with Team Lead CC’d.')}</span></u></b></p>`,
      '<p style="text-align: center;">&nbsp;</p>',
      `<p style="text-align: center;"><i><span style="font-family: 'Inter', serif;">${line('email.mobile_update.customer_advisory', 'Customer should be informed that we do not change mobile numbers on the call, and that the request will be sent to the relevant Department for processing and this could take 24 hours.')}</span></i></p>`,
      '<p style="text-align: center;">&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${line('email.mobile_update.turnaround_note', 'Mobile numbers will be updated within 24 hours for the customer.')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p style="text-align: justify;"><span style="font-family: 'Inter', serif;">${line('email.mobile_update.update_instruction', 'Please update the mobile number on Unibanks and TSYS for the following Customer(s)')}</span></p>`,
      '<p>&nbsp;</p>',
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      row(
        line('email.mobile_update.label_customer_name', 'Customer Name:'),
        name,
        true,
      ),
      row(
        line('email.mobile_update.label_customer_cif', 'Customer CIF:'),
        customerCif,
        false,
      ),
      // #866 — agent-completed: left blank (the in-context card is the Open24
      // card, not the VISA debit card this field asks for).
      row(
        line('email.mobile_update.label_card_number', 'Card Number(s):'),
        '',
        false,
      ),
      row(
        line(
          'email.mobile_update.label_new_mobile_number',
          'New Mobile Number:',
        ),
        mobile,
        false,
      ),
      row(
        line(
          'email.mobile_update.label_listened',
          'Name of person who listened to call:',
        ),
        '',
        false,
      ),
      row(
        line(
          'email.mobile_update.label_reason',
          'Reason for the change of mobile number:',
        ),
        '',
        false,
      ),
      '</table>',
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "SA Accounts - Change Personal Details Request" — the Enhanced Customer
 * Support callback request the agent raises when a South African account holder
 * needs a mobile-number or address change. Such changes are NOT actioned on the
 * call: the agent completes this template and the customer receives a callback
 * before 17:00 the following working day. Prefilled best-effort from the Change
 * Mobile Number screen's in-context detail (customer name, Open24 number and,
 * when the agent has captured it, the new mobile number); the Y/N update flags
 * and the new-address line are left blank for the agent. The source template
 * carries no fixed recipient (the agent addresses the ECL callback queue per the
 * process), so no `to:` is set.
 * Source: Drive "SCRN 26 / ECL ONLY callback for South African Accounts.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) whose
 * `buildHtml` reproduces the source `.msg` look-and-feel exactly (#434 exact-
 * match rework — the templates must match the originals). The source is Word-
 * authored HTML-encapsulated markup in the "Inter" font: a bold+underlined 16pt
 * title, two bold 12pt "Mobile Number Update:" / "Address Update:" lines each
 * ending in a PTSB-orange (#FC4C02) "Y/N", a bordered two-column table (#866)
 * holding the bold justified field labels (Customer Name / Open24 Number / New
 * Mobile Number / New Address) in the left cell and the prefilled value (or the
 * agent's blank space) in the right cell, and a closing PTSB-orange line whose
 * literal "**…**" markers wrap the bold "Please advise the customer they will
 * receive a call back before 17:00 the following working day" advisory. The
 * plain-text `build` carries the same content for the subject and as a mailto
 * fallback. Best-effort prefill (`customerName`, `open24Number`,
 * `newMobileNumber`) is interpolated with values HTML-escaped; the Y/N update
 * flags and the new-address line stay blank for the agent to complete. No `to:`
 * recipient is set (as with the plain-text `build`).
 *
 * #637 inc 19 — the fixed prose (subject, title, the two Y/N update lines, the
 * field LABELS and the closing callback advisory) is admin-editable via the
 * content catalogue (`email.sa_accounts_callback.*`, SCRN-26). Text only, never
 * logic: the auto-filled context values (`customerName` / `open24Number` /
 * `newMobileNumber`), the agent-blank lines and the exact-match Outlook layout/
 * fonts/colours/spacers stay fixed in code. Each default is byte-identical to
 * the previous compiled-in wording, so with no override the output is unchanged.
 */
export const SA_ACCOUNTS_CALLBACK: EmailTemplate = {
  id: 'sa-accounts-callback',
  label: 'SA Accounts Callback',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'EnhancedCustomerSupport@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({
    customerName = '',
    open24Number = '',
    newMobileNumber = '',
    content,
  }) => {
    // #637 inc 19 — the subject, the title, the two Y/N update lines, the field
    // LABELS and the closing callback advisory are admin-editable via the content
    // catalogue; the default passed here is byte-identical to the previous
    // compiled-in wording, so with no override the output is unchanged. The
    // auto-filled values (customerName / open24Number / newMobileNumber) and the
    // agent-blank lines stay fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line(
        'email.sa_accounts_callback.subject',
        'SA Accounts - Change Personal Details Request',
      ),
      body: [
        line(
          'email.sa_accounts_callback.title',
          'Enhanced Customer Support Callback – South African Accounts',
        ),
        ' ',
        `${line('email.sa_accounts_callback.label_mobile_update', 'Mobile Number Update:')} ${line('email.sa_accounts_callback.yn', 'Y/N')}`,
        `${line('email.sa_accounts_callback.label_address_update', 'Address Update:')} ${line('email.sa_accounts_callback.yn', 'Y/N')}`,
        ' ',
        `${line('email.sa_accounts_callback.label_customer_name', 'Customer Name:')} ${customerName}`,
        ' ',
        ' ',
        `${line('email.sa_accounts_callback.label_open24_number', 'Open24 Number:')} ${open24Number}`,
        ' ',
        ' ',
        `${line('email.sa_accounts_callback.label_new_mobile_number', 'New Mobile Number (if applicable):')} ${newMobileNumber}`,
        ' ',
        ' ',
        line(
          'email.sa_accounts_callback.label_new_address',
          'New Address (if applicable):',
        ),
        ' ',
        ' ',
        ' ',
        ' ',
        `**${line('email.sa_accounts_callback.callback_advisory', 'Please advise the customer they will receive a call back before 17:00 the following working day')}**`,
      ].join('\n'),
    };
  },
  buildHtml: ({
    customerName = '',
    open24Number = '',
    newMobileNumber = '',
    content,
  }) => {
    const name = escapeHtml(customerName);
    const open24 = escapeHtml(open24Number);
    const mobile = escapeHtml(newMobileNumber);
    // #637 inc 19 — resolve the editable wording; the default is byte-identical
    // to the previous compiled-in text. Escape the resolved value so an admin
    // reword can never inject markup into the exact-match Outlook HTML (the fixed
    // layout/fonts/colours/spacers stay in code).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // #866 — the source `.msg` holds the four fields in a bordered two-column
    // table: the bold + justified label in the LEFT cell (207.4pt) and the
    // prefilled value — or the agent's blank space — in the RIGHT cell
    // (238.65pt). The source pads the cells with blank spacer paragraphs (one
    // under the Customer Name / Open24 labels; two / four in the New Mobile /
    // New Address value cells), reproduced here for an exact match.
    const para = (text: string) =>
      `<p style="text-align: justify;"><b><span style="font-family: 'Inter', serif;">${text}</span></b></p>`;
    const spacer = para('&nbsp;');
    const cell = (width: string) =>
      `border: 1px solid #000000; padding: 0 5.4pt; vertical-align: top; width: ${width};`;
    const row = (
      label: string,
      value: string,
      labelSpacers: number,
      valueLines: number,
    ) =>
      [
        '<tr>',
        `<td style="${cell('207.4pt')}">${[para(label), ...Array<string>(labelSpacers).fill(spacer)].join('')}</td>`,
        `<td style="${cell('238.65pt')}">${[value ? para(value) : spacer, ...Array<string>(valueLines - 1).fill(spacer)].join('')}</td>`,
        '</tr>',
      ].join('\n');
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: \'Inter\', serif; font-size: 12pt; color: #000000;">',
      `<p><b><u><span style="font-size: 16pt; font-family: 'Inter', serif;">${line('email.sa_accounts_callback.title', 'Enhanced Customer Support Callback – South African Accounts')}</span></u></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.sa_accounts_callback.label_mobile_update', 'Mobile Number Update:')} <span style="color: #FC4C02;">${line('email.sa_accounts_callback.yn', 'Y/N')}</span></span></b></p>`,
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif;">${line('email.sa_accounts_callback.label_address_update', 'Address Update:')} <span style="color: #FC4C02;">${line('email.sa_accounts_callback.yn', 'Y/N')}</span></span></b></p>`,
      '<p><b><span style="font-size: 12pt; font-family: \'Inter\', serif; color: #FC4C02;">&nbsp;</span></b></p>',
      '<table style="border-collapse: collapse;">',
      row(
        line(
          'email.sa_accounts_callback.label_customer_name',
          'Customer Name:',
        ),
        name,
        1,
        1,
      ),
      row(
        line(
          'email.sa_accounts_callback.label_open24_number',
          'Open24 Number:',
        ),
        open24,
        1,
        1,
      ),
      row(
        line(
          'email.sa_accounts_callback.label_new_mobile_number',
          'New Mobile Number (if applicable):',
        ),
        mobile,
        0,
        2,
      ),
      row(
        line(
          'email.sa_accounts_callback.label_new_address',
          'New Address (if applicable):',
        ),
        '',
        0,
        4,
      ),
      '</table>',
      `<p><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">**<b>${line('email.sa_accounts_callback.callback_advisory', 'Please advise the customer they will receive a call back before 17:00 the following working day')}</b>**</span></p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/** Templates surfaced on the Change Mobile Number screen (SCRN-26). */
export const MOBILE_UPDATE_TEMPLATES: readonly EmailTemplate[] = [
  NEW_MOBILE_NUMBER_UPDATE,
  SA_ACCOUNTS_CALLBACK,
];

/**
 * "Special Category Data" — the record the agent sends to their Team Leader when
 * a customer shares sensitive personal data (e.g. health-related information)
 * during a call. It carries the *MUST READ* soft-introduction consent script the
 * agent reads to the customer, followed by the fields to record (agent name,
 * customer, CIF, SCD category, whether consent to record was given, the call
 * date/time and any notes). Sensitive information is never actioned on the call;
 * the agent completes this template and it is actioned by the Team Leader.
 * Prefilled best-effort from the Call History screen's in-context customer
 * (Customer Name + CIF); the recipient, agent name, SCD category, recorded
 * consent, call date/time and notes are left blank for the agent to complete
 * (the template addresses the agent's own Team Leader — "** INSERT TEAM LEADERS
 * NAME **" — so no fixed `to:` recipient is set).
 * Source: Drive "SCRN 13 / Special Category Data.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) whose
 * `buildHtml` reproduces the source `.msg` look-and-feel exactly (#434 exact-
 * match rework — the templates must match the originals). The source is Word-
 * authored HTML-encapsulated markup in the "Calibri" 11pt font (black, no PTSB-
 * orange accent in this template): the *MUST READ* soft-introduction consent
 * script is fully italic — a leading `*`**MUST READ**`*` (bold inside the
 * asterisks) then the smart-quoted opening paragraph in one paragraph, a
 * blank spacer, the smart-quoted consent-request paragraph, and the bold+italic
 * `If YES` / `If NO` lines (only the label is bold, the rest italic) — followed
 * by a bordered two-column (label | value) table (`If YES`/`If NO` label column
 * 99pt, value column 351.8pt) for Agent Name / Customer Name / CIF Number / SCD
 * Category / Recorded Consent to Record Data / Data and Time of Call / Notes.
 * The plain-text `build` carries the same content for the subject and as a
 * mailto fallback. Best-effort prefill (`customerName` → Customer Name value,
 * `cif` → CIF Number value) is interpolated with values HTML-escaped; the
 * recipient, agent name, SCD category, recorded consent, call date/time and
 * notes are left blank for the agent to complete. No fixed `to:` recipient is
 * set (the template addresses the agent's own Team Leader).
 */
export const SPECIAL_CATEGORY_DATA: EmailTemplate = {
  id: 'special-category-data',
  label: 'Special Category Data',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: '**YOUR TEAM LEADER**',
  build: ({ customerName = '', cif = '', content }) => {
    // #637 inc 26 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled `customerName` / `cif`, the `:` field-label
    // punctuation and the spacer lines stay fixed in code (text only, never
    // logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line(
        'email.special_category_data.subject',
        'Special Category Data',
      ),
      body: [
        line(
          'email.special_category_data.must_read_heading',
          '*MUST READ* SOFT INTRODUCTION',
        ),
        '',
        line(
          'email.special_category_data.intro',
          'Thank you for sharing this detail of your personal situation with me, I want to ensure what you have shared is recorded appropriately. Due to the nature of the information provided I must read you this statement. This can also be viewed through the Banks Data Protection Notice:',
        ),
        '',
        line(
          'email.special_category_data.consent_request',
          'I need to make you aware that the health-related information that you provided is considered personal data of a sensitive nature. Under Data Protection Law, your explicit consent is required in order for us to use and record this type of information which I understand you feel is relevant to your situation. Do you consent to PTSB collecting, using and storing this information on file?',
        ),
        '',
        line(
          'email.special_category_data.if_yes',
          'If YES: Thank you for confirming your consent.',
        ),
        '',
        line(
          'email.special_category_data.if_no',
          'If NO: If you do not confirm consent, I will not be able to take your information in to account when administering your account as there will be no record of your circumstances on file.',
        ),
        '',
        line('email.special_category_data.label_agent_name', 'Agent Name:'),
        '',
        `${line('email.special_category_data.label_customer_name', 'Customer Name:')} ${customerName}`,
        '',
        `${line('email.special_category_data.label_cif', 'CIF Number:')} ${cif}`,
        '',
        line('email.special_category_data.label_scd_category', 'SCD Category:'),
        '',
        line(
          'email.special_category_data.label_recorded_consent',
          'Recorded Consent to Record Data:',
        ),
        '',
        line(
          'email.special_category_data.label_call_datetime',
          'Data and Time of Call:',
        ),
        '',
        line('email.special_category_data.label_notes', 'Notes:'),
        '',
      ].join('\n'),
    };
  },
  // buildHtml reproduces the exact source formatting of the provided Outlook
  // template ("SCRN 13 / Special Category Data.msg", #434 — the templates must
  // match the originals). The source is Word-authored HTML-encapsulated markup
  // in the "Calibri" 11pt font (black; this template carries no PTSB-orange
  // accent). The consent script is fully italic; the leading `*MUST READ*` wraps
  // a bold "MUST READ", and "If YES"/"If NO" are bold+italic labels. The record
  // fields are a bordered two-column (label | value) table (label column 99pt,
  // value column 351.8pt) whose Customer Name / CIF Number values are prefilled
  // from context (HTML-escaped); the remaining values stay blank for the agent.
  buildHtml: ({ customerName = '', cif = '', content }) => {
    const name = escapeHtml(customerName);
    const cifNumber = escapeHtml(cif);
    // #637 inc 26 — resolve + HTML-escape the editable wording so an admin
    // reword can never inject markup into the exact-match Outlook HTML. Each
    // default is byte-identical to the current compiled-in text (smart quotes
    // U+201C/U+201D/U+2019 are left untouched by escapeHtml, so the output is
    // preserved). The fixed HTML tags/table markup/styles, the `<b>`/`<i>`
    // wrappers, the `:` punctuation, the blank value cells and the auto-filled
    // name + CIF stay in code. The If YES / If NO lines split into a bold label
    // key + a non-bold rest key so the fixed `<b>` wrapper stays in code.
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // A bordered table row: plain label cell (99pt) + value cell (351.8pt). The
    // first row keeps a full border; subsequent rows drop the shared top border,
    // exactly as the source MsoTableGrid renders.
    const labelCell = (label: string, first: boolean) =>
      `<td valign="top" style="width: 99pt; border: solid windowtext 1pt;${first ? '' : ' border-top: none;'} padding: 0cm 5.4pt;"><p><span style="font-family: Calibri, sans-serif;">${label}</span></p></td>`;
    const valueCell = (value: string, first: boolean) =>
      `<td valign="top" style="width: 351.8pt;${first ? ' border: solid windowtext 1pt; border-left: none;' : ' border-top: none; border-left: none; border-bottom: solid windowtext 1pt; border-right: solid windowtext 1pt;'} padding: 0cm 5.4pt;"><p><span style="font-family: Calibri, sans-serif;">${value || '&nbsp;'}</span></p></td>`;
    const row = (label: string, value: string, first: boolean) =>
      `<tr>${labelCell(label, first)}${valueCell(value, first)}</tr>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Calibri, sans-serif; font-size: 11pt; color: #000000;">',
      `<p><i>*<b>${line('email.special_category_data.must_read_bold', 'MUST READ')}</b>* ${line('email.special_category_data.html_intro', 'SOFT INTRODUCTION “Thank you for sharing this detail of your personal situation with me, I want to ensure what you have shared is recorded appropriately. Due to the nature of the information provided I must read you this statement. This can also be viewed through the Bank’s Data Protection Notice:”')}</i></p>`,
      '<p>&nbsp;</p>',
      `<p><i>${line('email.special_category_data.html_consent_request', '“I need to make you aware that the health-related information that you provided is considered personal data of a sensitive nature. Under Data Protection Law, your explicit consent is required in order for us to use and record this type of information which I understand you feel is relevant to your situation. Do you consent to PTSB collecting, using and storing this information on file?”')}</i></p>`,
      '<p>&nbsp;</p>',
      `<p><b><i>${line('email.special_category_data.if_yes_label', 'If YES')}</i></b><i>: ${line('email.special_category_data.if_yes_rest', 'Thank you for confirming your consent.')}</i></p>`,
      '<p>&nbsp;</p>',
      `<p><b><i>${line('email.special_category_data.if_no_label', 'If NO')}</i></b><i>: ${line('email.special_category_data.if_no_rest', 'If you do not confirm consent, I will not be able to take your information in to account when administering your account as there will be no record of your circumstances on file.')}</i></p>`,
      '<p>&nbsp;</p>',
      '<p>&nbsp;</p>',
      '<table border="1" cellspacing="0" cellpadding="0" style="border-collapse: collapse; border: none;">',
      row(
        line('email.special_category_data.label_agent_name', 'Agent Name:'),
        '',
        true,
      ),
      row(
        line(
          'email.special_category_data.label_customer_name',
          'Customer Name:',
        ),
        name,
        false,
      ),
      row(
        line('email.special_category_data.label_cif', 'CIF Number:'),
        cifNumber,
        false,
      ),
      row(
        line('email.special_category_data.label_scd_category', 'SCD Category:'),
        '',
        false,
      ),
      row(
        line(
          'email.special_category_data.label_recorded_consent',
          'Recorded Consent to Record Data:',
        ),
        '',
        false,
      ),
      row(
        line(
          'email.special_category_data.label_call_datetime',
          'Data and Time of Call:',
        ),
        '',
        false,
      ),
      row(line('email.special_category_data.label_notes', 'Notes:'), '', false),
      '</table>',
      '<p>&nbsp;</p>',
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/** Templates surfaced on the Call History screen (SCRN-13). */
export const CALL_HISTORY_TEMPLATES: readonly EmailTemplate[] = [
  SPECIAL_CATEGORY_DATA,
];

/**
 * "SEPA DD Recall" — request the SEPA queries team to return (recall) a SEPA
 * Direct Debit the customer has queried. Prefilled best-effort from the Direct
 * Debits screen's in-context detail (customer name, the debited account number,
 * the last-presented/posting date and the Direct Debit's Unibanks sequence
 * number); the customer contact number, amount and any field the app does not
 * hold are left as the source template's placeholder (`XXXXX` / `<...>`) for the
 * agent to complete. The customer must paste a screen-shot of the transaction
 * detail from the 27 screen, and only one recall may be requested per email.
 * Source: Drive "SCRN 33 & 34 / SEPA DD Recall.msg".
 *
 * `buildHtml` reproduces the source `.msg` look-and-feel exactly (#434 exact-
 * match rework — the templates must match the originals). The Drive source
 * ("SCRN 33 & 34 / SEPA DD Recall.msg") is Word-authored HTML in the "Calibri"
 * 11pt font over a dark-blue (`#000099`) body: a centred bold Arial-12pt
 * PTSB-orange (`#E46C0A`) title; bold + underlined dark-slate (`#44546A`)
 * "Customer Details;" / "Direct Debit Details;" section headers; the field lines
 * indented 18pt in the dark-blue body colour; a 14pt purple (`#7030A0`) "Only one
 * DD recall per email" line; and a bold 14pt red "Please paste screen shot…" line,
 * with the source's blank spacer paragraphs between items. The plain-text `build`
 * is retained for the subject and as a mailto fallback for the wording. Same
 * best-effort prefill; interpolated context values are HTML-escaped.
 *
 * #637 inc 20 — subject/title/section-headers/labels/notice/instruction/sign-off
 * are admin-editable via the content catalogue (`email.sepa_dd_recall.*`,
 * SCRN-33); defaults byte-identical to the previous compiled-in wording. The
 * auto-filled values (customerName / accountNumber / transactionAmount /
 * postingDate / sequenceNumber), the `<...>` placeholder fragments, the `to:`
 * recipient and the exact-match Outlook HTML layout stay fixed in code (text
 * only, never logic). In `buildHtml` every resolved value is HTML-escaped.
 */
/**
 * #854 — derive the National Sort Code (NSC) from a 14-digit PTSB account
 * number (`NSC(6) + AccountNumber(8)`, the same split the BFF's
 * `deriveSortCode` uses — issue #120), formatted `99-06-04`. Returns '' when
 * the value is not a 14-digit number so the caller keeps its placeholder rather
 * than showing a misleading partial.
 */
export function sortCodeFromAccount(accountNumber: string): string {
  const digits = (accountNumber ?? '').trim();
  if (!/^\d{14}$/.test(digits)) return '';
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
}

export const SEPA_DD_RECALL: EmailTemplate = {
  id: 'sepa-dd-recall',
  label: 'SEPA DD Recall',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'sepa.queries@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({
    customerName = '',
    accountNumber = '',
    transactionAmount = '',
    postingDate = '',
    sequenceNumber = '',
    content,
  }) => {
    // #637 inc 20 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled values, the `<...>` placeholder fragments and
    // the body structure stay fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      // #854 — @dylan219 (2026-10-19): the subject must carry the sort code as
      // well as the account number and amount, per the source template. The
      // sort code is the leading 6 digits (NSC) of the 14-digit PTSB account
      // number; it stays a placeholder only when it cannot be derived.
      subject: `${line('email.sepa_dd_recall.subject_prefix', 'SEPA DD Recall')} - ${
        sortCodeFromAccount(accountNumber) || '<sort code>'
      } - ${
        accountNumber || '<account number>'
      } - ${transactionAmount || '<amount>'}`,
      body: [
        line('email.sepa_dd_recall.title', 'SEPA DD Recall'),
        '',
        line(
          'email.sepa_dd_recall.header_customer_details',
          'Customer Details;',
        ),
        '',
        `${line('email.sepa_dd_recall.label_customer_name', 'Customer Name:')} ${customerName}`,
        '',
        `${line('email.sepa_dd_recall.label_customer_contact', 'Customer Contact Number:')} <customer contact number>`,
        '',
        line('email.sepa_dd_recall.header_dd_details', 'Direct Debit Details;'),
        '',
        `${line('email.sepa_dd_recall.label_account_number', 'Account Number (No Space/symbols):')} ${accountNumber}`,
        '',
        `${line('email.sepa_dd_recall.label_amount', 'Amount (no commas):')} ${transactionAmount}`,
        '',
        `${line('email.sepa_dd_recall.label_posting_date', 'Posting Date (dd/mm/yyyy):')} ${postingDate}`,
        '',
        `${line('email.sepa_dd_recall.label_sequence_number', 'Sequence Number (Incl zeros):')} ${sequenceNumber}`,
        '',
        line(
          'email.sepa_dd_recall.request_line',
          'Would it be possible for the SEPA DD outlined below to be returned?',
        ),
        '',
        line(
          'email.sepa_dd_recall.one_recall_notice',
          'Only one DD recall per email',
        ),
        '',
        line(
          'email.sepa_dd_recall.screenshot_instruction',
          'Please paste screen shot of transaction details from the 27 screen.  This screen shot should be completed using ctrl, alt and print screen',
        ),
        '',
        '',
        line(
          'email.sepa_dd_recall.screenshot_placeholder',
          '*** INSERT SCREEN SHOT HERE ***',
        ),
        '',
        '',
        line('email.sepa_dd_recall.sign_off', 'Kind Regards'),
        line('email.sepa_dd_recall.signature_placeholder', 'INSERT SIGNATURE'),
      ].join('\n'),
    };
  },
  buildHtml: ({
    customerName = '',
    accountNumber = '',
    transactionAmount = '',
    postingDate = '',
    sequenceNumber = '',
    content,
  }) => {
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    const amount = escapeHtml(transactionAmount);
    const posting = escapeHtml(postingDate);
    const sequence = escapeHtml(sequenceNumber);
    // #637 inc 20 — resolve + HTML-escape the editable wording so an admin
    // reword can never inject markup into the exact-match Outlook HTML; the
    // fixed layout/fonts/colours/spacers (incl. the `&nbsp;` in the screenshot
    // instruction) stay in code. Each default is byte-identical to the previous
    // compiled-in text.
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // The source .msg renders the screenshot instruction with a non-breaking
    // space after "screen." — a fixed layout detail, not editable text. The
    // catalogue default carries the two-space plain-text form, so restore the
    // `&nbsp;` for the HTML render (layout only, never new wording).
    const screenshotInstruction = line(
      'email.sepa_dd_recall.screenshot_instruction',
      'Please paste screen shot of transaction details from the 27 screen.  This screen shot should be completed using ctrl, alt and print screen',
    ).replace(/\. {2}/, '.&nbsp; ');
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Calibri, sans-serif; font-size: 11pt; color: #000099;">',
      `<p style="text-align: center;"><b><span style="font-family: Arial, sans-serif; font-size: 12pt; color: #E46C0A;">${line('email.sepa_dd_recall.title', 'SEPA DD Recall')}</span></b></p>`,
      '<p>&nbsp;</p>',
      '<p>&nbsp;</p>',
      `<p style="color: #44546A;"><b><u>${line('email.sepa_dd_recall.header_customer_details', 'Customer Details;')}</u></b></p>`,
      '<p>&nbsp;</p>',
      `<p style="margin-left: 18pt;">${line('email.sepa_dd_recall.label_customer_name', 'Customer Name:')} ${name}</p>`,
      '<p>&nbsp;</p>',
      `<p style="margin-left: 18pt;">${line('email.sepa_dd_recall.label_customer_contact', 'Customer Contact Number:')} &lt;customer contact number&gt;</p>`,
      '<p>&nbsp;</p>',
      `<p style="color: #44546A;"><b><u>${line('email.sepa_dd_recall.header_dd_details', 'Direct Debit Details;')}</u></b></p>`,
      '<p>&nbsp;</p>',
      `<p style="margin-left: 18pt;">${line('email.sepa_dd_recall.label_account_number', 'Account Number (No Space/symbols):')} ${account}</p>`,
      '<p>&nbsp;</p>',
      `<p style="margin-left: 18pt;">${line('email.sepa_dd_recall.label_amount', 'Amount (no commas):')} ${amount}</p>`,
      '<p>&nbsp;</p>',
      `<p style="margin-left: 18pt;">${line('email.sepa_dd_recall.label_posting_date', 'Posting Date (dd/mm/yyyy):')} ${posting}</p>`,
      '<p>&nbsp;</p>',
      `<p style="margin-left: 18pt;">${line('email.sepa_dd_recall.label_sequence_number', 'Sequence Number (Incl zeros):')} ${sequence}</p>`,
      '<p>&nbsp;</p>',
      `<p>${line('email.sepa_dd_recall.request_line', 'Would it be possible for the SEPA DD outlined below to be returned?')}</p>`,
      '<p>&nbsp;</p>',
      `<p style="font-size: 14pt; color: #7030A0;">${line('email.sepa_dd_recall.one_recall_notice', 'Only one DD recall per email')}</p>`,
      '<p>&nbsp;</p>',
      `<p style="font-size: 14pt; color: red;"><b>${screenshotInstruction}</b></p>`,
      '<p>&nbsp;</p>',
      '<p>&nbsp;</p>',
      `<p>${line('email.sepa_dd_recall.screenshot_placeholder', '*** INSERT SCREEN SHOT HERE ***')}</p>`,
      '<p>&nbsp;</p>',
      '<p>&nbsp;</p>',
      `<p>${line('email.sepa_dd_recall.sign_off', 'Kind Regards')}</p>`,
      `<p>${line('email.sepa_dd_recall.signature_placeholder', 'INSERT SIGNATURE')}</p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * Templates surfaced on the Direct Debits screen (SCRN-33). #771 (@dylan219,
 * "Yeah sure add it to screen 33") adds Missing Payments + SEPA Compliance
 * alongside the existing SEPA DD Recall and Proof of Payment. The two new
 * templates are declared later in this file (they group with the SEPA CT
 * payment-screen templates), so this array is assembled below where all ids are
 * in scope ({@link DIRECT_DEBITS_TEMPLATES}).
 */

/**
 * "Clear Cheque Counter" — request the Current Accounts Department to clear a
 * customer's cheque counter to zero. The agent works through the checklist
 * (customer consent, holds/address checks, charges confirmed, timeframe, that
 * clearing the counter does not cancel existing cheques) and confirms whether a
 * new chequebook should also be issued. Prefilled best-effort from the
 * Chequebook screen's in-context detail (customer name + selected account
 * number); every Y/N answer and the "issue a chequebook" decision are left blank
 * for the agent to complete on the call, verbatim from the source template.
 * `buildHtml` — the plain-text `build` is retained for the subject and as a
 * mailto fallback; `buildHtml` delivers the formatted downloadable Outlook
 * draft (Option A exact-match, #434), reproducing the source verbatim: the Inter
 * font throughout, a bold+underline 16pt #1D242C title, a bordered 2-row table
 * with bold 11pt "Customer Name:" / "Account Number:" labels, and six bold 12pt
 * #1D242C justified question paragraphs each ending in a bold orange (#FC4C02)
 * "Y/N". Interpolated context values are HTML-escaped.
 * Source: Drive "SCRN 41 / Clear Cheque Counter.msg".
 */
export const CLEAR_CHEQUE_COUNTER: EmailTemplate = {
  id: 'clear-cheque-counter',
  label: 'Clear Cheque Counter',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'CurrentAccounts@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', accountNumber = '', content }) => {
    // #637 inc 23 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled `customerName` / `accountNumber` values, the
    // subject's ` - <colleague name> - <numeric reference>` placeholders (#854), the appended ` Y/N` question
    // markers and the blank spacer lines stay fixed in code (text only, never
    // logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      // #854 — @dylan219 (2026-10-19): the subject is the stem followed by a
      // colleague name and a numeric reference, both left as placeholders for
      // the agent to fill in (the customer name/account stay in the body only).
      subject: `${line(
        'email.clear_cheque_counter.subject',
        'Cheque counter clearance Request',
      )} - <colleague name> - <numeric reference>`,
      body: [
        line(
          'email.clear_cheque_counter.title',
          'Cheque Counter Clearance Request',
        ),
        '',
        `${line('email.clear_cheque_counter.label_customer_name', 'Customer Name:')} ${customerName}`,
        '',
        '',
        `${line('email.clear_cheque_counter.label_account_number', 'Account Number:')} ${accountNumber}`,
        '',
        '',
        '',
        `${line('email.clear_cheque_counter.question_consent', 'Has the customer given their consent to have the counter cleared to zero?')} Y/N`,
        '',
        `${line('email.clear_cheque_counter.question_screens', 'Have you checked the 34/35/24 screens for holds, alternate addresses and the correct address for the chequebook to be sent to?')} Y/N`,
        '',
        `${line('email.clear_cheque_counter.question_charges', 'Have you confirmed the charges to the customer? 50c GSD per cheque + 20c Cheque Issue Fee per cheque?')} Y/N`,
        '',
        `${line('email.clear_cheque_counter.question_timeframe', 'Have you confirmed the timeframe of 5-7 working days for customer to receive chequebook?')} Y/N`,
        '',
        `${line('email.clear_cheque_counter.question_still_active', 'Have you confirmed to the customer that this does not cancel the cheques & they will still be active until the customer cancels them?')} Y/N`,
        '',
        `${line('email.clear_cheque_counter.question_issue_chequebook', 'Once counter has been cleared do you require Current Accounts to issue a chequebook?')} Y/N`,
        '',
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', accountNumber = '', content }) => {
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    // Reproduces the Outlook source verbatim: Inter font throughout; a
    // bold+underline 16pt #1D242C title; a bordered 2-row table with bold 11pt
    // "Customer Name:" / "Account Number:" labels and value cells; then six
    // bold 12pt #1D242C justified question paragraphs, each ending in a bold
    // 12pt orange (#FC4C02) "Y/N".
    // #637 inc 23 — resolve + HTML-escape the editable wording so an admin reword
    // can never inject markup into the exact-match Outlook HTML; the fixed
    // layout/fonts/colours/spacers/table, the trailing space after each question
    // and the separate orange `Y/N` span (added by `question()`) stay in code.
    // Each default is byte-identical to the previous compiled-in text (the
    // plain-text form; the `&` in the still-active question is escaped to `&amp;`
    // exactly as before).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const labelCell = (text: string) =>
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-top: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${text}</span></b></p></td>`;
    const valueCell = (val: string, lastCol = false) =>
      lastCol
        ? `<td valign="top" style="width: 207.4pt; border-top: none; border-left: none; border-bottom: solid #000000 1pt; border-right: solid #000000 1pt; padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${val}</span></p></td>`
        : `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-left: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${val}</span></p></td>`;
    const question = (text: string) =>
      `<p style="text-align: justify;"><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #1D242C;">${text} </span><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">Y/N</span></b></p>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      `<body style="font-family: 'Inter', serif; font-size: 12pt; color: #1D242C;">`,
      `<p style="text-align: justify;"><b><u><span style="font-size: 16pt; font-family: 'Inter', serif; color: #1D242C;">${line('email.clear_cheque_counter.title', 'Cheque Counter Clearance Request')}</span></u></b></p>`,
      '<p>&nbsp;</p>',
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      // First row carries the full top border. The label's trailing space is a
      // fixed layout detail kept in code.
      `<tr><td valign="top" style="width: 207.4pt; border: solid #000000 1pt; padding: 0cm 5.4pt;"><p style="text-align: justify;"><b><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${line('email.clear_cheque_counter.label_customer_name', 'Customer Name:')} </span></b></p></td><td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-left: none; padding: 0cm 5.4pt;"><p style="text-align: justify;"><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${name}</span></p></td></tr>`,
      `<tr>${labelCell(line('email.clear_cheque_counter.label_account_number', 'Account Number:'))}${valueCell(account, true)}</tr>`,
      '</table>',
      '<p>&nbsp;</p>',
      question(
        line(
          'email.clear_cheque_counter.question_consent',
          'Has the customer given their consent to have the counter cleared to zero?',
        ),
      ),
      '<p>&nbsp;</p>',
      question(
        line(
          'email.clear_cheque_counter.question_screens',
          'Have you checked the 34/35/24 screens for holds, alternate addresses and the correct address for the chequebook to be sent to?',
        ),
      ),
      '<p>&nbsp;</p>',
      question(
        line(
          'email.clear_cheque_counter.question_charges',
          'Have you confirmed the charges to the customer? 50c GSD per cheque + 20c Cheque Issue Fee per cheque?',
        ),
      ),
      '<p>&nbsp;</p>',
      question(
        line(
          'email.clear_cheque_counter.question_timeframe',
          'Have you confirmed the timeframe of 5-7 working days for customer to receive chequebook?',
        ),
      ),
      '<p>&nbsp;</p>',
      question(
        line(
          'email.clear_cheque_counter.question_still_active',
          'Have you confirmed to the customer that this does not cancel the cheques & they will still be active until the customer cancels them?',
        ),
      ),
      '<p>&nbsp;</p>',
      question(
        line(
          'email.clear_cheque_counter.question_issue_chequebook',
          'Once counter has been cleared do you require Current Accounts to issue a chequebook?',
        ),
      ),
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/** Templates surfaced on the Chequebook screen (SCRN-41). */
export const CHEQUEBOOK_TEMPLATES: readonly EmailTemplate[] = [
  CLEAR_CHEQUE_COUNTER,
];

/**
 * "SEPA CT Recall" — request the SEPA queries team to recall (return) a SEPA
 * Credit Transfer the customer has queried (e.g. wrong amount / wrong account).
 * The draft carries the advisory the agent reads to the customer (recall not
 * guaranteed, one recall per transaction, confirm address, 20-business-day
 * response window, letters issued, third-party follow-up) verbatim from the
 * source template, followed by the fields to record. Prefilled best-effort from
 * the Standing Orders screen's in-context detail (customer name and the debited
 * account number); the customer contact number and the reason-for-recall are
 * left for the agent to complete, and the amount / sort-code / account-number
 * detail is captured by the required screenshot of the transaction from the 27
 * screen. Only one recall may be requested per transaction. Targets the SEPA
 * queries team ("Sepa Queries").
 *
 * `buildHtml` reproduces the source `.msg` look-and-feel exactly (#434 exact-
 * match rework — the templates must match the originals). The Drive source
 * ("SCRN 28, 31, 35, 36 & 37 / SEPA CT Recall.msg") is Word-authored HTML in the
 * PTSB brand "Inter" font over a dark-slate (`#1D242C`) body: a bold + underlined
 * 16pt "SEPA Credit Transfer Recall Request" title; a 12pt "Please advise the
 * customer:" line; the advisory + reason-for-recall items as bold 12pt PTSB-orange
 * (`#FC4C02`) bullets (each source line prefixed with the literal `* ` and tab);
 * an italic 12pt "Updates on a recall request…" line; a bordered 2×2 table with
 * the bold "Customer Name:" / "Customer's Contact Number:" labels in the left
 * column and the prefilled value in the right column (#866); a bold + underlined 12pt "Reason for Recall (highlight in red
 * below):" header; a 12pt "Would it be possible…" line; and a bold 12pt PTSB-orange
 * "INSERT SCREENSHOT…" line, with the source's blank spacer paragraphs between
 * items. The plain-text `build` is retained for the subject and as a mailto
 * fallback for the wording. Same best-effort prefill; interpolated context values
 * are HTML-escaped.
 * Source: Drive "SCRN 28, 31, 35, 36 & 37 / SEPA CT Recall.msg".
 *
 * #637 inc 21 — subject/title/advise-intro/the six advisory bullets/updates
 * line/field labels/reason header/the three reason options/recall request line/
 * screenshot instruction are admin-editable via the content catalogue
 * (`email.sepa_ct_recall.*`, SCRN-35); defaults byte-identical to the previous
 * compiled-in wording (the plain-text form). The auto-filled `customerName`
 * value, the exact-match Outlook HTML layout, and the HTML-only layout details
 * (the trailing-space trims after "customer:" / "confirm same" + the
 * `3<sup>rd</sup>` superscript) stay fixed in code. In `buildHtml` every resolved
 * value is HTML-escaped.
 */
/**
 * #866 — fill the SEPA CT Recall / Authorisation-Refusal subject from the
 * in-context account number. The source subjects end in the agent placeholder
 * "*******insert sort code & account number & amount *******"; when the account
 * number is known that placeholder is replaced by
 * `<sort code> - <account number> - <amount>` (the same shape as the sibling
 * SEPA DD Recall subject, #854), the sort code being derived from the PTSB
 * account number and the amount staying an `<amount>` placeholder (it is not
 * held on these screens). The account-number segment carries the 8-digit
 * account number ONLY — the sort code is stripped from it so it appears once,
 * in its own segment (#866, @dylan219 2026-10-29); see `splitSortCodeAccount`.
 * With no account number — or when an admin subject override carries no such
 * placeholder — the resolved subject is returned verbatim.
 */
export function sepaCtSubject(resolved: string, accountNumber: string): string {
  const account = (accountNumber ?? '').trim();
  const placeholder = /\s*\*+\s*insert[^*]*\*+\s*$/i;
  if (!account || !placeholder.test(resolved)) return resolved;
  const base = resolved.replace(placeholder, '').trimEnd();
  const split = splitSortCodeAccount(account);
  return `${base} - ${split.sortCode || '<sort code>'} - ${split.accountNumber} - <amount>`;
}

/**
 * #866 — split an in-context PTSB account identifier into its National Sort
 * Code and the bare 8-digit account number, so neither is shown twice. The
 * Standing Orders / Bill Payments screens pass the `customer/accounts/list`
 * `AccountNumber`, i.e. the 14-digit BBAN `NSC(6) + AccountNumber(8)` (the BFF's
 * `deriveSortCode` / `toDisplayAccount` split, #120 / Q5); an Irish IBAN
 * (`IEkk BBBB NNNNNN AAAAAAAA`, the same BBAN behind a country + check-digit +
 * bank-code prefix) is accepted too. Spaces / dashes are tolerated. Any other
 * value (e.g. a bare 8-digit account number) yields no sort code and the value
 * unchanged, so the caller keeps its `<sort code>` placeholder.
 */
export function splitSortCodeAccount(value: string): {
  sortCode: string;
  accountNumber: string;
} {
  const raw = (value ?? '').trim();
  const compact = raw.replace(/[\s-]/g, '').toUpperCase();
  const bban = /^\d{14}$/.test(compact)
    ? compact
    : /^IE\d{2}[A-Z]{4}\d{14}$/.test(compact)
      ? compact.slice(8)
      : '';
  if (!bban) return { sortCode: '', accountNumber: raw };
  return {
    sortCode: sortCodeFromAccount(bban),
    accountNumber: bban.slice(6),
  };
}

/**
 * #866 — one row of the SEPA CT bordered field table, per the source `.msg`:
 * the bold label in the LEFT cell and the (bold) value — or a blank spacer when
 * there is none — in the RIGHT cell. Both arguments must already be
 * HTML-escaped.
 */
function sepaCtFieldRow(label: string, value: string): string {
  const cell =
    'border: 1px solid #000000; padding: 0 5.4pt; vertical-align: top; width: 207.4pt;';
  return [
    '<tr>',
    `<td style="${cell}"><p><b>${label}</b></p></td>`,
    `<td style="${cell}"><p>${value ? `<b>${value}</b>` : '&nbsp;'}</p></td>`,
    '</tr>',
  ].join('\n');
}

export const SEPA_CT_RECALL: EmailTemplate = {
  id: 'sepa-ct-recall',
  label: 'SEPA CT Recall',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'sepa.queries@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', accountNumber = '', content }) => {
    // #637 inc 21 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled `customerName` value and the body structure
    // (the `*\t` bullet prefixes, the blank spacer lines) stay fixed in code
    // (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      // #866 — the account number (and derived sort code) fill the subject's
      // agent placeholder where known.
      subject: sepaCtSubject(
        line(
          'email.sepa_ct_recall.subject',
          'SEPA CT Recall Request *******insert sort code & account number & amount *******',
        ),
        accountNumber,
      ),
      body: [
        line(
          'email.sepa_ct_recall.title',
          'SEPA Credit Transfer Recall Request',
        ),
        line(
          'email.sepa_ct_recall.advise_intro',
          'Please advise the customer: ',
        ),
        `*\t${line('email.sepa_ct_recall.advisory_not_guaranteed', 'The recall is not guaranteed')}`,
        `*\t${line('email.sepa_ct_recall.advisory_one_per_transaction', 'Only one recall request can be made per transaction')}`,
        `*\t${line('email.sepa_ct_recall.advisory_confirm_address', 'Please confirm the customers address')}`,
        `*\t${line('email.sepa_ct_recall.advisory_20_days_response', 'We will process the recall if we receive a response from the 3rd party bank within 20 business days. A letter would be issued to the customer to confirm same ')}`,
        `*\t${line('email.sepa_ct_recall.advisory_20_days_no_response', 'If we do not receive a response from the 3rd party bank within 20 business days we will issue a letter to the customer confirming the recall request was rejected')}`,
        `*\t${line('email.sepa_ct_recall.advisory_raise_with_third_party', 'If the recall is rejected then the customer must raise this with the third party directly themselves')}`,
        ' ',
        line(
          'email.sepa_ct_recall.updates_line',
          'Updates on a recall request can be checked on the 26 screen. Current accounts will place a note once the request is raised and when there is an update on the request',
        ),
        ' ',
        `${line('email.sepa_ct_recall.label_customer_name', 'Customer Name:')} ${customerName}`,
        ' ',
        ' ',
        line(
          'email.sepa_ct_recall.label_customer_contact',
          'Customer’s Contact Number:',
        ),
        ' ',
        ' ',
        ' ',
        line(
          'email.sepa_ct_recall.reason_header',
          'Reason for Recall (highlight in red below): ',
        ),
        `*\t${line('email.sepa_ct_recall.reason_wrong_amount', 'Wrong Amount')}`,
        `*\t${line('email.sepa_ct_recall.reason_wrong_account', 'Wrong Account/Invalid IBAN')}`,
        `*\t${line('email.sepa_ct_recall.reason_other', 'Other (please provide reason)')}`,
        ' ',
        line(
          'email.sepa_ct_recall.request_line',
          'Would it be possible for the SEPA CT outlined below to be returned?',
        ),
        ' ',
        line(
          'email.sepa_ct_recall.screenshot_instruction',
          'INSERT SCREENSHOT OF THE TRANSACTION IN QUESTION FROM THE 27 SCREEN HERE',
        ),
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', content }) => {
    const name = escapeHtml(customerName);
    // #637 inc 21 — resolve + HTML-escape the editable wording so an admin
    // reword can never inject markup into the exact-match Outlook HTML; the fixed
    // layout/fonts/colours/spacers/bullets/table stay in code. Each default is
    // byte-identical to the previous compiled-in text (the plain-text form).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // The catalogue defaults carry the plain-text form. Restore the HTML-only
    // layout details (layout only, never new wording): the advise-intro and the
    // "confirm same" bullet drop their trailing space in the HTML render, and the
    // "3rd" ordinal is superscripted in the no-response bullet.
    const adviseIntro = line(
      'email.sepa_ct_recall.advise_intro',
      'Please advise the customer: ',
    ).replace(/ $/, '');
    const advisory20DaysResponse = line(
      'email.sepa_ct_recall.advisory_20_days_response',
      'We will process the recall if we receive a response from the 3rd party bank within 20 business days. A letter would be issued to the customer to confirm same ',
    ).replace(/ $/, '');
    const advisory20DaysNoResponse = line(
      'email.sepa_ct_recall.advisory_20_days_no_response',
      'If we do not receive a response from the 3rd party bank within 20 business days we will issue a letter to the customer confirming the recall request was rejected',
    ).replace('3rd party', '3<sup>rd</sup> party');
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Inter, sans-serif; font-size: 12pt; color: #1D242C;">',
      `<p><b><u><span style="font-size: 16pt;">${line('email.sepa_ct_recall.title', 'SEPA Credit Transfer Recall Request')}</span></u></b></p>`,
      `<p>${adviseIntro}</p>`,
      '<ul style="color: #FC4C02;">',
      `<li><b>${line('email.sepa_ct_recall.advisory_not_guaranteed', 'The recall is not guaranteed')}</b></li>`,
      `<li><b>${line('email.sepa_ct_recall.advisory_one_per_transaction', 'Only one recall request can be made per transaction')}</b></li>`,
      `<li><b>${line('email.sepa_ct_recall.advisory_confirm_address', 'Please confirm the customers address')}</b></li>`,
      `<li><b>${advisory20DaysResponse}</b></li>`,
      `<li><b>${advisory20DaysNoResponse}</b></li>`,
      `<li><b>${line('email.sepa_ct_recall.advisory_raise_with_third_party', 'If the recall is rejected then the customer must raise this with the third party directly themselves')}</b></li>`,
      '</ul>',
      '<p>&nbsp;</p>',
      `<p><i>${line('email.sepa_ct_recall.updates_line', 'Updates on a recall request can be checked on the 26 screen. Current accounts will place a note once the request is raised and when there is an update on the request')}</i></p>`,
      '<p>&nbsp;</p>',
      // #866 — label in the left cell, value in the right cell (per source).
      '<table style="border-collapse: collapse;">',
      sepaCtFieldRow(
        line('email.sepa_ct_recall.label_customer_name', 'Customer Name:'),
        name,
      ),
      sepaCtFieldRow(
        line(
          'email.sepa_ct_recall.label_customer_contact',
          'Customer’s Contact Number:',
        ),
        '',
      ),
      '</table>',
      '<p>&nbsp;</p>',
      `<p><b><u>${line('email.sepa_ct_recall.reason_header', 'Reason for Recall (highlight in red below): ')}</u></b></p>`,
      '<ul style="color: #FC4C02;">',
      `<li><b>${line('email.sepa_ct_recall.reason_wrong_amount', 'Wrong Amount')}</b></li>`,
      `<li><b>${line('email.sepa_ct_recall.reason_wrong_account', 'Wrong Account/Invalid IBAN')}</b></li>`,
      `<li><b>${line('email.sepa_ct_recall.reason_other', 'Other (please provide reason)')}</b></li>`,
      '</ul>',
      '<p>&nbsp;</p>',
      `<p>${line('email.sepa_ct_recall.request_line', 'Would it be possible for the SEPA CT outlined below to be returned?')}</p>`,
      '<p>&nbsp;</p>',
      `<p><b><span style="color: #FC4C02;">${line('email.sepa_ct_recall.screenshot_instruction', 'INSERT SCREENSHOT OF THE TRANSACTION IN QUESTION FROM THE 27 SCREEN HERE')}</span></b></p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "Authorisation/Refusal of SEPA CT Recall" — the response the agent records
 * when a customer has received a letter from PTSB asking them to authorise (or
 * refuse) a SEPA Credit Transfer recall request. The draft carries the advisory
 * ("if approved, we will process the recall refund from their account today")
 * and the fields to record (customer name / account / amount / date / contact
 * number) followed by the highlight-in-red Authorised / Refused response,
 * verbatim from the source template. Prefilled best-effort from the in-context
 * customer name + selected account number; the amount / date, contact number
 * and the Authorised/Refused response are left for the agent to complete.
 * Targets the SEPA queries team ("Sepa Queries", matching the existing
 * `sepa-ct-recall`); the source also CCs Telephone Banking, addressed by the
 * agent from Outlook.
 * `buildHtml` — the plain-text `build` is retained for the subject and as a
 * mailto fallback; `buildHtml` delivers the formatted downloadable Outlook
 * draft (Option A, #434), preserving the source look-and-feel (bold title,
 * advisory paragraphs, bold field labels, Authorised/Refused response list).
 * Source: Drive "SCRN 28, 31, 35, 36 & 37 / Authorisation-Refusal of SEPA CT Recall.msg".
 */
export const SEPA_CT_AUTH_REFUSAL: EmailTemplate = {
  id: 'sepa-ct-auth-refusal',
  label: 'SEPA CT Recall Authorisation/Refusal',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'sepa.queries@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', accountNumber = '', content }) => {
    // #637 inc 22 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled `customerName` / `accountNumber` values and the
    // body structure (the `*\t` bullet prefixes, the blank spacer lines) stay
    // fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      // #866 — the account number (and derived sort code) fill the subject's
      // agent placeholder where known.
      subject: sepaCtSubject(
        line(
          'email.sepa_ct_auth_refusal.subject',
          'Response to Recall Request *******insert sort code & account number & amount *******',
        ),
        accountNumber,
      ),
      body: [
        line(
          'email.sepa_ct_auth_refusal.title',
          'Authorisation/Refusal of SEPA Credit Transfer Recall Request',
        ),
        ' ',
        line(
          'email.sepa_ct_auth_refusal.if_approved',
          'If approved, please advise the customer that we will process the recall refund from their account today',
        ),
        ' ',
        line(
          'email.sepa_ct_auth_refusal.authorisation_context',
          'Where a customer has received a letter from PTSB requesting authorisation to a recall request',
        ),
        ' ',
        `${line('email.sepa_ct_auth_refusal.label_customer_name', 'Customer Name:')} ${customerName}`,
        ' ',
        ' ',
        `${line('email.sepa_ct_auth_refusal.label_account_number', 'Account Number:')}${accountNumber ? ` ${accountNumber}` : ''}`,
        ' ',
        ' ',
        line('email.sepa_ct_auth_refusal.label_amount', 'Amount:'),
        ' ',
        ' ',
        line('email.sepa_ct_auth_refusal.label_date', 'Date:'),
        ' ',
        ' ',
        line(
          'email.sepa_ct_auth_refusal.label_customer_contact',
          'Customer’s Contact Number:',
        ),
        ' ',
        ' ',
        ' ',
        line(
          'email.sepa_ct_auth_refusal.response_header',
          'Customer Response (highlight response in red)',
        ),
        ' ',
        `*\t${line('email.sepa_ct_auth_refusal.response_authorised', 'Authorised - customer agrees to recall request (positive)')}`,
        `*\t${line('email.sepa_ct_auth_refusal.response_refused', 'Refused - customer declines recall request (negative)')}`,
        ' ',
        ' ',
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', accountNumber = '', content }) => {
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    // Exact-match rework (#434): reproduces the source Outlook draft "SCRN 28,
    // 31, 35, 36 & 37 / Authorisation-Refusal of SEPA CT Recall.msg" verbatim,
    // matching the sibling SEPA_CT_RECALL exact-match style — Inter brand font
    // over a dark-slate (#1D242C) body, PTSB-orange (#FC4C02) for the source's
    // red-emphasised advisory + the "highlight response in red" section, a
    // bordered field table, and a bold + underlined 16pt title. The source
    // renders the "If approved" advisory bold-orange and the response-list
    // header + 16pt response items likewise.
    // #637 inc 22 — resolve + HTML-escape the editable wording so an admin
    // reword can never inject markup into the exact-match Outlook HTML; the fixed
    // layout/fonts/colours/spacers/bullets/table stay in code. Each default is
    // byte-identical to the previous compiled-in text (the plain-text form).
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // The catalogue defaults carry the plain-text form. Restore the HTML-only
    // layout detail (layout only, never new wording): the source bolds the
    // Authorised/Refused lead word within each 16pt response item.
    const responseAuthorised = line(
      'email.sepa_ct_auth_refusal.response_authorised',
      'Authorised - customer agrees to recall request (positive)',
    ).replace('Authorised', '<b>Authorised</b>');
    const responseRefused = line(
      'email.sepa_ct_auth_refusal.response_refused',
      'Refused - customer declines recall request (negative)',
    ).replace('Refused', '<b>Refused</b>');
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Inter, sans-serif; font-size: 12pt; color: #1D242C;">',
      `<p><b><u><span style="font-size: 16pt;">${line('email.sepa_ct_auth_refusal.title', 'Authorisation/Refusal of SEPA Credit Transfer Recall Request')}</span></u></b></p>`,
      `<p><b><span style="color: #FC4C02;">${line('email.sepa_ct_auth_refusal.if_approved', 'If approved, please advise the customer that we will process the recall refund from their account today')}</span></b></p>`,
      `<p>${line('email.sepa_ct_auth_refusal.authorisation_context', 'Where a customer has received a letter from PTSB requesting authorisation to a recall request')}</p>`,
      '<p>&nbsp;</p>',
      // #866 — label in the left cell, value in the right cell (per source).
      '<table style="border-collapse: collapse;">',
      sepaCtFieldRow(
        line(
          'email.sepa_ct_auth_refusal.label_customer_name',
          'Customer Name:',
        ),
        name,
      ),
      sepaCtFieldRow(
        line(
          'email.sepa_ct_auth_refusal.label_account_number',
          'Account Number:',
        ),
        account,
      ),
      sepaCtFieldRow(
        line('email.sepa_ct_auth_refusal.label_amount', 'Amount:'),
        '',
      ),
      sepaCtFieldRow(
        line('email.sepa_ct_auth_refusal.label_date', 'Date:'),
        '',
      ),
      sepaCtFieldRow(
        line(
          'email.sepa_ct_auth_refusal.label_customer_contact',
          'Customer’s Contact Number:',
        ),
        '',
      ),
      '</table>',
      '<p>&nbsp;</p>',
      `<p><b><span style="font-size: 16pt; color: #FC4C02;">${line('email.sepa_ct_auth_refusal.response_header', 'Customer Response (highlight response in red)')}</span></b></p>`,
      '<ul>',
      `<li><span style="font-size: 16pt;">${responseAuthorised}</span></li>`,
      `<li><span style="font-size: 16pt;">${responseRefused}</span></li>`,
      '</ul>',
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "Missing Payments" — request the SEPA queries team to put a trace on a
 * transaction the customer says did not arrive. Prefilled best-effort from the
 * payment screen's in-context detail (customer name → Account Name, selected
 * account number); the per-transaction fields (contact number, date, amount,
 * reference, the "made successfully before?" answer) and the screenshot stay
 * blank for the agent to complete on the call.
 * Source: Drive "SCRN 28, 31, 35, 36 & 37 / Missing Payments - account number.msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` — the plain-text `build` is retained for the subject/recipient and
 * as a mailto fallback for the wording. The `MISSING PAYMENTS` title is rendered
 * bold + underlined; the labelled fields carry bold labels in a bordered table
 * matching the sibling SEPA templates; interpolated context values are
 * HTML-escaped. #771 (@dylan219).
 *
 * #637 — the title (also the subject), the seven field labels, the screenshot
 * instruction, the "Request for" header and the trace-request line are
 * admin-editable via the content catalogue (`email.missing_payments.*`,
 * SCRN-46); each default is the source wording verbatim. The auto-filled
 * customer name / account number, the `Label: value` structure, the blank
 * spacer lines and the exact-match Outlook HTML stay fixed in code (text only,
 * never logic).
 */
export const MISSING_PAYMENTS: EmailTemplate = {
  id: 'missing-payments',
  label: 'Missing Payments',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'sepa.queries@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', accountNumber = '', content }) => {
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      // #854 — the account number is carried in the subject where known
      // ("Missing Payments - account number").
      subject: `${line('email.missing_payments.subject', 'Missing Payments')}${
        accountNumber ? ` - ${accountNumber}` : ''
      }`,
      body: [
        line('email.missing_payments.title', 'MISSING PAYMENTS'),
        '',
        `${line('email.missing_payments.label_account_no', 'Account No:')} ${accountNumber}`,
        '',
        `${line('email.missing_payments.label_account_name', 'Account Name:')} ${customerName}`,
        '',
        line('email.missing_payments.label_contact_number', 'Contact number:'),
        '',
        line('email.missing_payments.label_date', 'Date of Trx:'),
        '',
        line('email.missing_payments.label_amount', 'Amount of Trx:'),
        '',
        line('email.missing_payments.label_reference', 'Reference:'),
        '',
        line(
          'email.missing_payments.label_made_before',
          'Has the payment been made successfully before?',
        ),
        '',
        line(
          'email.missing_payments.screenshot_instruction',
          'ScreenShot of transaction blown up on Uniscreens:',
        ),
        '',
        line('email.missing_payments.request_header', 'Request for'),
        '',
        line(
          'email.missing_payments.request_line',
          'Can you please put a trace on the above transaction?',
        ),
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', accountNumber = '', content }) => {
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const name = escapeHtml(customerName);
    const account = escapeHtml(accountNumber);
    const labelCell = (text: string, value: string) =>
      `<tr><td style="border: 1px solid #000000; padding: 0 5.4pt; vertical-align: top; width: 207.4pt;"><p><b>${text}</b></p></td><td style="border: 1px solid #000000; padding: 0 5.4pt; vertical-align: top; width: 207.4pt;"><p>${value || '&nbsp;'}</p></td></tr>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Inter, sans-serif; font-size: 12pt; color: #1D242C;">',
      `<p><b><u><span style="font-size: 16pt;">${line('email.missing_payments.title', 'MISSING PAYMENTS')}</span></u></b></p>`,
      '<p>&nbsp;</p>',
      '<table style="border-collapse: collapse;">',
      labelCell(
        line('email.missing_payments.label_account_no', 'Account No:'),
        account,
      ),
      labelCell(
        line('email.missing_payments.label_account_name', 'Account Name:'),
        name,
      ),
      labelCell(
        line('email.missing_payments.label_contact_number', 'Contact number:'),
        '',
      ),
      labelCell(line('email.missing_payments.label_date', 'Date of Trx:'), ''),
      labelCell(
        line('email.missing_payments.label_amount', 'Amount of Trx:'),
        '',
      ),
      labelCell(
        line('email.missing_payments.label_reference', 'Reference:'),
        '',
      ),
      labelCell(
        line(
          'email.missing_payments.label_made_before',
          'Has the payment been made successfully before?',
        ),
        '',
      ),
      '</table>',
      '<p>&nbsp;</p>',
      `<p><b><span style="color: #FC4C02;">${line('email.missing_payments.screenshot_instruction', 'ScreenShot of transaction blown up on Uniscreens:')}</span></b></p>`,
      '<p>&nbsp;</p>',
      `<p><b><u>${line('email.missing_payments.request_header', 'Request for')}</u></b></p>`,
      `<p>${line('email.missing_payments.request_line', 'Can you please put a trace on the above transaction?')}</p>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "SEPA Compliance Query" — request the SEPA Compliance team to review and
 * confirm the status of an incoming SEPA payment held for compliance. The
 * source template's fields (Debtor/Creditor IBAN, date, amount, reference) are
 * per-transaction and not carried in the in-context detail, so they are left
 * blank for the agent to complete on the call; nothing is prefilled.
 * Source: Drive "SCRN 28, 31, 35, 36 & 37 / SEPA Compliance (3).msg".
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` — the plain-text `build` is retained for the subject/recipient and
 * as a mailto fallback for the wording. The `SEPA Compliance Query` title is
 * rendered bold; the labelled fields carry bold labels matching the sibling
 * SEPA templates. #771 (@dylan219).
 *
 * #637 — the subject, the title, the review-request line and the five field
 * labels are admin-editable via the content catalogue
 * (`email.sepa_compliance.*`, SCRN-46); each default is the source wording
 * verbatim. The `Label:` structure, the blank spacer lines and the exact-match
 * Outlook HTML stay fixed in code (text only, never logic).
 */
export const SEPA_COMPLIANCE: EmailTemplate = {
  id: 'sepa-compliance',
  label: 'SEPA Compliance',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'sepacompliancequeries@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: ({ content }) => {
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line(
        'email.sepa_compliance.subject',
        'Potential incoming SEPA PAYMENT held for compliance - **insert full sort code and account number and amount**',
      ),
      body: [
        line('email.sepa_compliance.title', 'SEPA Compliance Query'),
        line(
          'email.sepa_compliance.request_line',
          'Can you please review and confirm the status of the below incoming SEPA Payment?',
        ),
        '',
        line(
          'email.sepa_compliance.label_debtor_iban',
          'Debtor (Other Bank) IBAN:',
        ),
        '',
        '',
        line(
          'email.sepa_compliance.label_creditor_iban',
          'Creditor (PTSB) IBAN:',
        ),
        '',
        '',
        line('email.sepa_compliance.label_date', 'Date of Transaction:'),
        '',
        '',
        line('email.sepa_compliance.label_amount', 'Amount:'),
        '',
        '',
        line('email.sepa_compliance.label_reference', 'Reference:'),
        '',
      ].join('\n'),
    };
  },
  buildHtml: ({ content }) => {
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const labelRow = (text: string) =>
      `<tr><td style="border: 1px solid #000000; padding: 0 5.4pt; vertical-align: top; width: 207.4pt;"><p><b>${text}</b></p></td><td style="border: 1px solid #000000; padding: 0 5.4pt; vertical-align: top; width: 207.4pt;"><p>&nbsp;</p></td></tr>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Inter, sans-serif; font-size: 12pt; color: #1D242C;">',
      `<p><b><u><span style="font-size: 16pt;">${line('email.sepa_compliance.title', 'SEPA Compliance Query')}</span></u></b></p>`,
      `<p>${line('email.sepa_compliance.request_line', 'Can you please review and confirm the status of the below incoming SEPA Payment?')}</p>`,
      '<p>&nbsp;</p>',
      '<table style="border-collapse: collapse;">',
      labelRow(
        line(
          'email.sepa_compliance.label_debtor_iban',
          'Debtor (Other Bank) IBAN:',
        ),
      ),
      labelRow(
        line(
          'email.sepa_compliance.label_creditor_iban',
          'Creditor (PTSB) IBAN:',
        ),
      ),
      labelRow(
        line('email.sepa_compliance.label_date', 'Date of Transaction:'),
      ),
      labelRow(line('email.sepa_compliance.label_amount', 'Amount:')),
      labelRow(line('email.sepa_compliance.label_reference', 'Reference:')),
      '</table>',
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * Templates surfaced on both the Standing Orders screen (SCRN-35/36/37) and the
 * Bill Payments screen (SCRN-28/31): the SEPA CT Recall request and its
 * Authorisation/Refusal response counterpart. Per @dylan219 the
 * Authorisation/Refusal template is placed on SCRN 28, 31, 35, 36 & 37 — the
 * same five screens that render this array — so it surfaces alongside the recall
 * on both surfaces automatically.
 *
 * #771 (@dylan219) — the two new Missing Payments + SEPA Compliance templates
 * and the existing Proof of Payment are added to these payment screens (SCRN 28,
 * 31, 35, 36, 37) as well as the Transaction screens (SCRN 46, 47).
 */
export const STANDING_ORDERS_TEMPLATES: readonly EmailTemplate[] = [
  SEPA_CT_RECALL,
  SEPA_CT_AUTH_REFUSAL,
  // #771 — Proof of Payment is offered on every payment screen's dropdown; this
  // array backs both Standing Orders (SCRN-35) and Bill Payments (SCRN-28).
  // Shared with the Transaction Information screen (SCRN-47) — prefilled
  // best-effort from the in-context customer name + account number.
  PROOF_OF_PAYMENT,
  MISSING_PAYMENTS,
  SEPA_COMPLIANCE,
];

/**
 * Templates surfaced on the Transaction Information screen (SCRN-47). #771
 * (@dylan219) — Proof of Payment plus the two new Missing Payments + SEPA
 * Compliance templates, matching the payment screens (SCRN 28/31/35/36/37).
 */
export const TRANSACTION_INFORMATION_TEMPLATES: readonly EmailTemplate[] = [
  PROOF_OF_PAYMENT,
  // #795 (@dylan219): the drill-down (SCRN-47) must offer the SAME email
  // templates as the Transactions search/listing (SCRN-46), which surfaces the
  // Remove Merchant Block template alongside the three payment templates. Order
  // matches the listing's menu (Proof of Payment, Remove Merchant Block,
  // Missing Payments, SEPA Compliance). All the drill-down's in-context detail
  // (customer name, account number, content catalogue) already satisfies this
  // template's best-effort prefill; the card number stays blank (PCI) and the
  // generated `.eml` filename is keyed on the subject (never the CIF, #738).
  REMOVE_MERCHANT_BLOCK,
  MISSING_PAYMENTS,
  SEPA_COMPLIANCE,
];

/**
 * Templates surfaced on the Direct Debits screen (SCRN-33). #771 (@dylan219)
 * — the DD-specific SEPA DD Recall plus the three payment templates (Proof of
 * Payment and the two new Missing Payments + SEPA Compliance), matching the
 * other payment screens (SCRN 28/31/35/36/37). Declared here so the two new
 * template ids are in scope.
 */
export const DIRECT_DEBITS_TEMPLATES: readonly EmailTemplate[] = [
  SEPA_DD_RECALL,
  PROOF_OF_PAYMENT,
  MISSING_PAYMENTS,
  SEPA_COMPLIANCE,
];

/**
 * "Gambling Block" — request the agent's Team Leader to add or remove a gambling
 * block on the customer's card(s). Adding a block applies within 2 hours (after
 * which gambling transactions are blocked online and in-store); removing a block
 * takes up to 48 hours (after which the card(s) can be used for gambling again).
 * The draft carries the add/remove timing advisory verbatim from the source
 * template, followed by the fields to record. Prefilled best-effort from the
 * Card Maintenance screen's in-context detail (customer name and the selected
 * card, masked to last-4 — a full PAN is NEVER rendered); the Add/Remove Block
 * choice is left blank for the agent to complete. The source template addresses
 * the agent's own Team Leader ("**Add Team Leader name here**"), so no fixed
 * `to:` recipient is set.
 *
 * `buildHtml` reproduces the source `.msg` look-and-feel exactly (#434 exact-
 * match rework — the templates must match the originals). The Drive source
 * ("SCRN 50 / Gambling Block.msg") is Word-authored HTML in the PTSB brand
 * "Inter" font: a bold + underlined 16pt "Gambling Block" title; bold + italic
 * 12pt PTSB-orange (`#FC4C02`) "Adding:" / "Removing:" section labels, each
 * followed by two bold 12pt orange timing-advisory lines; a plain 11pt
 * justified "Please action the gambling block for the following card(s)" line;
 * then a bordered 4-row table (`windowtext`/`#000000` 1pt borders) with bold
 * 11pt "Customer Name:", "Card Number(s):", "Add/Remove Block:" and
 * "Security Complete (Y/N):" labels and their value cells. The plain-text
 * `build` (subject + mailto fallback) is retained unchanged.
 * The card is rendered in full per @dylan219's #434 authorisation (email
 * templates only — see fullCard); interpolated context values are HTML-escaped.
 * Source: Drive "SCRN 50 / Gambling Block.msg".
 *
 * #637 inc 24 — the fixed prose (subject, title, the `Adding:` / `Removing:`
 * section labels, the four timing-advisory lines, the intro line and the four
 * table field labels) is admin-editable via the content catalogue
 * (`email.gambling_block.*`); every default here is byte-identical to the
 * previous compiled-in wording, so with no override the draft is unchanged. The
 * auto-filled customer name, the full card number, the blank Add/Remove &
 * Security value cells and the exact-match Outlook HTML stay fixed in code (text
 * only, never logic).
 *
 * A follow-up increment adds the **EDS Data Breach** template (Drive folder
 * "SCRN 23, 24 & 39" — "EVERYDAY DIGITAL SUPPORT Data Breach.msg"), wired into
 * the Change Customer Details screen (SCRN-24), replacing the earlier
 * "coming soon" stub.
 */
export const GAMBLING_BLOCK: EmailTemplate = {
  id: 'gambling-block',
  label: 'Gambling Block',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: '**YOUR TEAM LEADER**',
  cc: 'RetailDigitalChannels@ptsb.ie, TelephoneBanking@ptsb.ie',
  build: ({ customerName = '', cardNumber, content }) => {
    // #637 inc 24 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled `customerName`, the full `cardNumber` (per
    // @dylan219 #434 email-only authorisation), the blank Add/Remove line and the
    // spacer lines stay fixed in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line('email.gambling_block.subject', 'Gambling Block'),
      body: [
        line('email.gambling_block.title', 'Gambling Block'),
        '',
        line('email.gambling_block.adding_label', 'Adding:'),
        line(
          'email.gambling_block.adding_line1',
          'Gambling block will be applied within 2 hours of the request.',
        ),
        line(
          'email.gambling_block.adding_line2',
          'After this, transactions identified to PTSB as gambling will be blocked both online and in-store.',
        ),
        '',
        line('email.gambling_block.removing_label', 'Removing:'),
        line(
          'email.gambling_block.removing_line1',
          'Gambling block will be removed within 48 hours of the request.',
        ),
        line(
          'email.gambling_block.removing_line2',
          'After this, the card(s) can be used again for gambling transactions.',
        ),
        '',
        line(
          'email.gambling_block.intro',
          'Please action the gambling block for the following card(s)',
        ),
        `${line('email.gambling_block.label_customer_name', 'Customer Name:')} ${customerName}`,
        `${line('email.gambling_block.label_card_number', 'Card Number(s):')} ${fullCard(cardNumber)}`,
        line('email.gambling_block.label_add_remove', 'Add/Remove Block:'),
        '',
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', cardNumber, content }) => {
    const name = escapeHtml(customerName);
    // Full 16-digit card number per @dylan219's #434 authorisation (email
    // templates only — see fullCard). Still HTML-escaped.
    const card = escapeHtml(fullCard(cardNumber));
    // #637 inc 24 — resolve + HTML-escape the editable wording so an admin reword
    // can never inject markup into the exact-match Outlook HTML. Each default is
    // byte-identical to the current compiled-in text; the fixed layout/fonts/
    // colours/spacers/table, the blank Add/Remove & Security value cells and the
    // auto-filled name + full card stay in code.
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // Reproduces the Outlook source verbatim: Inter font throughout; a
    // bold+underline 16pt title; bold+italic 12pt orange (#FC4C02) "Adding:" /
    // "Removing:" section labels each followed by two bold 12pt orange advisory
    // lines; a plain 11pt justified intro line; then a bordered 4-row table
    // (#000000 1pt borders) of bold 11pt labels and value cells.
    const orange = (text: string) =>
      `<p><b><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${text}</span></b></p>`;
    const orangeLabel = (text: string) =>
      `<p><b><i><span style="font-size: 12pt; font-family: 'Inter', serif; color: #FC4C02;">${text}</span></i></b></p>`;
    const labelCell = (text: string, firstRow: boolean) =>
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt;${
        firstRow ? '' : ' border-top: none;'
      } padding: 0cm 5.4pt;"><p style="line-height: 106%;"><b><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${text}</span></b></p></td>`;
    const valueCell = (val: string, firstRow: boolean) =>
      `<td valign="top" style="width: 207.4pt; border: solid #000000 1pt; border-left: none;${
        firstRow ? '' : ' border-top: none;'
      } padding: 0cm 5.4pt;"><p style="text-align: justify; line-height: 106%;"><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${val}</span></p></td>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      `<body style="font-family: 'Inter', serif; font-size: 12pt; color: #1D242C;">`,
      `<p><b><u><span style="font-size: 16pt; font-family: 'Inter', serif; color: #1D242C;">${line('email.gambling_block.title', 'Gambling Block')}</span></u></b></p>`,
      orangeLabel(line('email.gambling_block.adding_label', 'Adding:')),
      orange(
        line(
          'email.gambling_block.adding_line1',
          'Gambling block will be applied within 2 hours of the request.',
        ),
      ),
      orange(
        line(
          'email.gambling_block.adding_line2',
          'After this, transactions identified to PTSB as gambling will be blocked both online and in-store.',
        ),
      ),
      '<p>&nbsp;</p>',
      orangeLabel(line('email.gambling_block.removing_label', 'Removing:')),
      orange(
        line(
          'email.gambling_block.removing_line1',
          'Gambling block will be removed within 48 hours of the request.',
        ),
      ),
      orange(
        line(
          'email.gambling_block.removing_line2',
          'After this, the card(s) can be used again for gambling transactions.',
        ),
      ),
      `<p style="text-align: justify;"><span style="font-size: 11pt; font-family: 'Inter', serif; color: #1D242C;">${line('email.gambling_block.intro', 'Please action the gambling block for the following card(s)')}</span></p>`,
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      `<tr>${labelCell(line('email.gambling_block.label_customer_name', 'Customer Name:'), true)}${valueCell(name, true)}</tr>`,
      `<tr>${labelCell(line('email.gambling_block.label_card_number', 'Card Number(s):'), false)}${valueCell(card, false)}</tr>`,
      `<tr>${labelCell(line('email.gambling_block.label_add_remove', 'Add/Remove Block:'), false)}${valueCell('&nbsp;', false)}</tr>`,
      `<tr>${labelCell(line('email.gambling_block.label_security_complete', 'Security Complete (Y/N):'), false)}${valueCell('&nbsp;', false)}</tr>`,
      '</table>',
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "Travel Spend" (a.k.a. "Travel Note Request") — the request the agent sends to
 * the Telephone Banking travel-note robot to place a travel note on a customer's
 * card so card spend is authorised while they are abroad. The draft is a
 * **robot-read** format: the automation reads the field block between the
 * "ROBOT READS FROM HERE" / "TO HERE" markers, so the field lines and the
 * instruction/location reference below them are reproduced verbatim from the
 * source template. Prefilled best-effort from the Card Maintenance screen's
 * in-context detail: the customer CIF (entered as a bare number, per the robot's
 * instruction) and the selected card's last-4 digits (never a full PAN — only
 * the last 4 are rendered, exactly as the robot requires). The travel start/end
 * dates and location are left blank for the agent to complete on the call. The
 * source template addresses the "Travel Note" travel-note mailbox by display
 * name only (no resolvable SMTP address), so no fixed `to:` recipient is set.
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml`; the plain-text `build` is retained for the subject and as the
 * `mailto:` fallback. Because this is a **robot-read** template (the automation
 * parses the ROBOT-READS field block and the whitespace-aligned location table),
 * the `.eml` preserves the entire body VERBATIM inside a `<pre>` (HTML-escaped,
 * `white-space: pre`) — not reflowed into `<p>`/`<strong>` like the other
 * templates. The RTF source is Arial 10pt (`\f0\fs20`) black throughout, so the
 * `<pre>` renders Arial 10pt (not monospace) to match the source look-and-feel;
 * `white-space: pre` keeps the literal spaces + line breaks intact, so the field
 * block stays byte-for-byte parseable by the robot. Both seams derive from the
 * shared {@link travelSpendBodyLines} so they never diverge.
 * Source: Drive "SCRN 50 / Travel Spend.msg".
 *
 * #637 inc 28 — the human-facing prose (greeting, sign-off, the INSTRUCTIONS
 * header + four instruction lines, the locations-list header) is admin-editable
 * via the content catalogue (`email.travel_spend.*`); every default is
 * byte-identical to the previous compiled-in wording, so with no override the
 * draft is unchanged. The ROBOT-READS marker block, the machine-read field
 * labels/values and the whitespace-aligned country lookup table stay FIXED in
 * code so the downstream travel-note robot's parse is never broken (text only,
 * never logic — and never the machine-read format).
 */
/**
 * Builds the verbatim Travel-Note body lines, shared by the plain-text `build`
 * and the Option A `.eml` `buildHtml`. The downstream travel-note automation
 * reads the field block between the ROBOT-READS markers, so the lines (and the
 * whitespace-aligned location reference) are reproduced exactly — the two
 * delivery seams must never diverge.
 */
function travelSpendBodyLines({
  cif = '',
  cardNumber,
  content,
}: TemplateContext): string[] {
  // The travel-note robot requires the last 4 digits only (no bullets/spaces),
  // so we render the trailing 4 digits directly rather than the masked
  // `•••• 1234` form. A full PAN is never rendered.
  const lastFour = (cardNumber ?? '').replace(/\D/g, '').slice(-4);
  // #637 inc 28 — resolve the human-facing prose (greeting, sign-off,
  // instructions, locations header) from the content catalogue; each default is
  // byte-identical to the previous compiled-in text, so with no override the
  // body is unchanged. The ROBOT-READS marker block, the field labels
  // (CIF/LastFourDigits/StartDate/EndDate/Location) + their values, and the
  // whitespace-aligned country lookup table stay FIXED in code — they are
  // parsed byte-for-byte by the downstream travel-note robot (text only, never
  // logic; and never the machine-read format).
  const line = (key: string, def: string): string => content?.[key] ?? def;
  return [
    line('email.travel_spend.greeting', 'Hello,'),
    '',
    line('email.travel_spend.request_line', 'Request Below.'),
    '',
    '----------ROBOT READS FROM HERE----------',
    `CIF:  ${cif}`,
    `LastFourDigits:  ${lastFour}`,
    'StartDate:  ',
    'EndDate:  ',
    'Location:  ',
    '---------------------TO HERE--------------------- ',
    '',
    line('email.travel_spend.sign_off', 'Kind regards,'),
    line('email.travel_spend.sign_off_name', 'Human'),
    '',
    '',
    line('email.travel_spend.instructions_header', 'INSTRUCTIONS:'),
    line(
      'email.travel_spend.instruction_1',
      "1) The 'CIF' number is the unique customer ID number (available on Uniscreens) - this should be entered as a number (no characters or spaces) ",
    ),
    line(
      'email.travel_spend.instruction_2',
      "2) The 'LastFourDigits' refers to the last four digits of the card number.  This should be entered as 4 digits only with no other characters (e.g. ***)",
    ),
    line(
      'email.travel_spend.instruction_3',
      '3) The start date and end date should be entered in the format DD/MM/YYYY',
    ),
    line(
      'email.travel_spend.instruction_4',
      '4) The Location will be matched against the list below (the Robot will not be able to understand if there are any spelling errors!)',
    ),
    '',
    line(
      'email.travel_spend.locations_header',
      'List of Locations (as of 13/05/2019)',
    ),
    'AFGHANISTAN                                  COOK ISLANDS                                  INDONESIA                         NETHERLAND ANTILLES SOUTH KOREA',
    'ALBANIA                                              COSTA RICA                                        IRAN                                      NETHERLANDS                                  SPAIN',
    "ALGERIA                                              COTE D'IVOIRE                                  IRAQ                                      NEW CALEDONIA                             SRI LANKA",
    'AMERICAN SAMOA                        CROATIA                                              ISLE OF MAN                      NEW ZEALAND                                  ST KITTS & NEVIS',
    'ANDORRA                                           CUBA                                                    ISRAEL                                  NICARAGUA                                      ST PIERRE & MIQUELON',
    'ANGOLA                                              CYPRUS                                                ITALY                                     NIGER                                                   ST. HELENA',
    'ANGUILLA                                           CZECH REPUBLIC                              JAMAICA                             NIGERIA                                               SUDAN',
    'ANTARCTICA                                      DEM REP OF CONGO                      JAPAN                                  NIUE                                                      SURINAM',
    'ANTIGUA AND BARBUDA             DENMARK                                           JERSEY                                  NORFOLK ISLAND                            SVALBARD & JAN MAYEN',
    'ARGENTINA                                       DJIBOUTI                                             JORDAN                               NORTH KOREA                                  SWAZILAND',
    'ARMENIA                                            DOMINICA                                          KAZAKHSTAN                    NORTHERN MARIANA ISL                SWEDEN',
    'ARUBA                                                 DOMINICAN REPUBLIC                  KENYA                                  NORWAY                                             SWITZERLAND',
    'AUSTRALIA                                         ECUADOR                                            KIRIBATI                               OMAN                                                  SYRIA',
    'AUSTRIA                                              EGYPT                                                   KUWAIT                               PAKISTAN                                           TAIWAN',
    'AZERBAIJAN                                       EL SALVADOR                                    KYRGYZSTAN                     PALAU                                                  TAJIKISTAN',
    'BAHAMAS                                           EQUATORIAL GUINEA                    LAO                                        PALESTINE                                          TANZANIA',
    'BAHRAIN                                             ERITREA                                               LATVIA                                 PANAMA                                             THAILAND',
    'BANGLADESH                                    ESTONIA                                              LEBANON                            PAPUA NEW GUINEA                     TIMOR-LESTE',
    'BARBADOS                                         ETHIOPIA                                             LESOTHO                             PARAGUAY                                         TOGO',
    'BELARUS                                              FALKLAND ISLANDS                        LIBERIA                 PERU                                                     TOKELAU',
    'BELGIUM                                             FAROE ISLANDS                                LIBYA                                     PHILIPPINES                                       TONGA',
    'BELIZE                                                   FIJI                                                         LIECHTENSTEIN PITCAIRN                                             TRINIDAD & TOBAGO',
    'BENIN                                                   FINLAND                                              LITHUANIA                         POLAND                                               TUNISIA',
    'BERMUDA                                           FRANCE                                                LUXEMBOURG                  PORTUGAL                                         TURKEY',
    'BHUTAN                                              FRENCH GUIANA                             MACAO                                PUERTO RICO                                    TURKMENISTAN',
    'BOLIVIA                                               FRENCH POLYNESIA                        MACEDONIA                      QATAR                                                  TURKS & CAICOS ISL',
    'BOSNIA                                                FRENCH SOUTHERN TER                MADAGASCAR                  REUNION                                            TUVALU',
    'BOTSWANA                                        GABON                                                                MALAWI                              ROMANIA                                           UGANDA',
    'BOUVET ISLAND                               GAMBIA                                              MALAYSIA                           RUSSIAN FEDERATION                   UKRAINE',
    'BR. INDIAN OCEAN TER GEORGIA                                             MALDIVES                           RWANDA                                             UNITED ARAB EMIRATES',
    'BRAZIL                                                  GERMANY                                           MALI                                     SAINT BARTHELEMY                        UNITED KINGDOM',
    'BRITISH VIRGIN ISL                          GHANA                                                                MALTA                                 SAINT LUCIA                                      URUGUAY',
    'BRUNEI DARUSSALAM                  GIBRALTAR                                         MARSHALL ISLANDS       SAINT MAARTENS                           US MINOR OUTLYING IS',
    'BULGARIA                                           GREECE                                                MARTINIQUE                     SAINT MARTIN (FRENCH               US VIRIGIN ISLANDS',
    'BURKINA FASO                                 GREENLAND                                       MAURITANIA                    SAINT VINCENT                                USA',
    'BURUNDI                                            GRENADA                                           MAURITIUS                        SAMOA                                                                UZBEKISTAN',
    'CAMBODIA                                         GUADELOUPE                                   MAYOTTE                            SAN MARINO                                    VANUATU',
    'CAMEROON                                       GUAM                                                  MEXICO                               SAO TOME & PRINCIPE                  VATICAN CITY',
    'CANADA                                              GUATEMALA                                     MICRONESIA                     SAUDI ARABIA                                  VENEZUELA',
    'CAPE VERDE                                       GUERNSEY                                          MOLDOVA                          SENEGAL                                             VIETNAM',
    'CAYMAN ISLANDS                           GUINEA                                               MONACO                            SERBIA                                                  WALLIS & FUTUNA',
    'CENTRAL AFRICAN REP.                GUINEA - BISSAU                             MONGOLIA                        SEYCHELLES                                        WESTERN SAHARA',
    'CHAD                                                    GUYANA                                              MONTENEGRO                 SIERRA LEONE                                   YEMEN',
    'CHILE                                                     HAITI                                                     MONTSERRAT                   SINGAPORE                                        YUGOSLAVIA',
    'CHINA                                                   HEARD & MCDONALD ISL             MOROCCO                          SLOVAKIA                                           ZAIRE',
    'CHRISTMAS ISLAND                        HONDURAS                                        MOZAMBIQUE                  SLOVENIA                                           ZAMBIA',
    'COCOS ISLANDS                               HONG KONG                                     MYANMAR                         SOLOMON ISLANDS                        ZIMBABWE',
    'COLOMBIA                                         HUNGARY                                           NAMIBIA                             SOMALIA             ',
    'COMOROS                                          ICELAND                                              NAURU                                                SOUTH AFRICA  ',
    'CONGO                                                INDIA                                                    NEPAL                                   SOUTH GEORGIA             ',
    '',
    '',
    '',
  ];
}

export const TRAVEL_SPEND: EmailTemplate = {
  id: 'travel-spend',
  label: 'Travel Spend',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: 'travel.note@ptsb.ie',
  cc: 'TelephoneBanking@ptsb.ie',
  build: (ctx) => ({
    subject: 'Travel Note Request ',
    body: travelSpendBodyLines(ctx).join('\n'),
  }),
  buildHtml: (ctx) => {
    // Robot-read template: the downstream travel-note automation reads the field
    // block between the ROBOT-READS markers, and the location reference is a
    // whitespace-aligned table — so we preserve the ENTIRE source body VERBATIM
    // inside a <pre> (HTML-escaped, white-space: pre), rather than reflowing it
    // into <p>/<strong> like the other templates. The RTF source is Arial 10pt
    // (\f0\fs20) black throughout, so we render Arial 10pt (not monospace); the
    // <pre>'s white-space: pre keeps the literal spaces + line breaks intact, so
    // the field block stays byte-for-byte parseable by the robot.
    const body = travelSpendBodyLines(ctx).join('\n');
    return [
      '<!DOCTYPE html>',
      '<html>',
      '<body style="font-family: Arial, sans-serif; font-size: 10pt; color: #000000;">',
      `<pre style="font-family: Arial, sans-serif; font-size: 10pt; margin: 0; white-space: pre;">${escapeHtml(body)}</pre>`,
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "Apple/Google Pay Token" — the request the agent sends to the Technical
 * Transformation Support team to activate a customer's Apple Pay / Google Pay
 * token on their card. The draft carries the activation request, the customer
 * name / VSDR (Visa debit) card field lines, and the three mandatory security
 * questions (securely identified / passed Extra Security / mobile number amended
 * in the past 72 hours) verbatim from the source template, followed by the
 * source signature + data-protection footer. Prefilled best-effort from the Card
 * Maintenance screen's in-context detail: the customer name and the selected
 * card's last-4 (masked — a full PAN is NEVER rendered); the three Yes/No
 * security answers are left as the template's own text for the agent to complete
 * on the call. The source template addresses the Technical Transformation
 * Support mailbox by display name only (no resolvable SMTP address), so no fixed
 * `to:` recipient is set.
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml`; the plain-text `build` is retained for the subject and as the
 * `mailto:` fallback. `buildHtml` reproduces the Outlook source VERBATIM:
 * **Aptos, sans-serif, 11pt, colour #1D252D** applied to the whole body (this
 * source uses Aptos, not the Inter of some siblings); a plain `Hi,` +
 * activation-request paragraph pair; then a bordered **2×2** field table
 * (Customer Name / VSDR Number) and a bordered **3×2** security-questions table
 * (securely identified / passed Extra Security / mobile amended in the past 72
 * hours → Yes/No), both `border-collapse` with 1pt solid #000000 cell borders
 * and `0 5.4pt` cell padding, all cells plain `MsoNormal` (labels NOT bold) —
 * matching the source look-and-feel — followed by a trailing `Kind Regards,`
 * paragraph. The value cells are pre-filled best-effort from the Card
 * Maintenance in-context detail: the customer name and the full 16-digit VSDR
 * (card) number per @dylan219's #434 authorisation (email templates only — see
 * `fullCard`); every interpolated value is HTML-escaped.
 * Source: Drive "SCRN 50 / AppleGoogle Pay Token.msg".
 *
 * #637 inc 25 — the fixed prose (subject, greeting, the activation-request line,
 * the two field labels, the three security-question prompts, the Yes/No answer
 * prompt and the sign-off) is admin-editable via the content catalogue
 * (`email.apple_google_pay.*`); every default here is byte-identical to the
 * previous compiled-in wording, so with no override the draft is unchanged. The
 * auto-filled customer name, the full card number, the `:` field-label
 * punctuation, the blank answer cells and the exact-match Outlook HTML stay
 * fixed in code (text only, never logic); HTML overrides are escaped so a reword
 * cannot inject markup.
 */
export const APPLE_GOOGLE_PAY_TOKEN: EmailTemplate = {
  id: 'apple-google-pay-token',
  label: 'Apple/Google Pay Token',
  // #854 — recipients per @dylan219's 2026-10-19 configuration review.
  to: '**YOUR TEAM LEADER**',
  build: ({ customerName = '', cardNumber, content }) => {
    // #637 inc 25 — resolve the editable wording; each default is byte-identical
    // to the previous compiled-in text, so with no override the output is
    // unchanged. The auto-filled `customerName` + full `cardNumber` (per
    // @dylan219 #434 email-only authorisation), the `:` field-label punctuation,
    // the blank Yes/No answer prompts left for the agent and the fixed spacer
    // lines stay in code (text only, never logic).
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: line('email.apple_google_pay.subject', 'Apple/Google Pay Token'),
      body: [
        line('email.apple_google_pay.greeting', 'Hi,'),
        ' ',
        line(
          'email.apple_google_pay.activation_request',
          'Can you please activate the below customer’s apple/google pay token?',
        ),
        ' ',
        `${line('email.apple_google_pay.label_customer_name', 'Customer Name')}: ${customerName}`,
        '',
        `${line('email.apple_google_pay.label_vsdr_number', 'VSDR Number')}: ${fullCard(cardNumber)}`,
        '',
        ' ',
        ' ',
        line(
          'email.apple_google_pay.q_identified',
          'Has the customer been securely identified?',
        ),
        line('email.apple_google_pay.yes_no', 'Yes/No'),
        line(
          'email.apple_google_pay.q_extra_security',
          'Has the customer passed Extra Security?',
        ),
        line('email.apple_google_pay.yes_no', 'Yes/No'),
        line(
          'email.apple_google_pay.q_mobile_amended',
          'Has the customer amended their mobile number in the past 72 hours? (16 screen)',
        ),
        line('email.apple_google_pay.yes_no', 'Yes/No'),
        ' ',
        ' ',
        ' ',
        line('email.apple_google_pay.sign_off', 'Kind Regards,'),
        '',
      ].join('\n'),
    };
  },
  buildHtml: ({ customerName = '', cardNumber, content }) => {
    const name = escapeHtml(customerName);
    // Full 16-digit card number per @dylan219's #434 authorisation (email
    // templates only — see fullCard). Still HTML-escaped.
    const card = escapeHtml(fullCard(cardNumber));
    // #637 inc 25 — resolve + HTML-escape the editable wording so an admin reword
    // can never inject markup into the exact-match Outlook HTML. Each default is
    // byte-identical to the current compiled-in text; the fixed Aptos layout, the
    // two bordered tables, the Yes/No answer prompts and the auto-filled name +
    // full card stay in code.
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    // Reproduces the Outlook source verbatim: Aptos 11pt #1D252D throughout
    // (this source uses Aptos, not Inter); plain (non-bold) body paragraphs; and
    // two bordered tables (#000000 1pt borders, border-collapse, 0 5.4pt cell
    // padding, all cells plain MsoNormal — labels NOT bold): a 2×2 field table
    // (Customer Name / VSDR Number) and a 3×2 security-questions table.
    const base =
      "font-size: 11pt; font-family: 'Aptos', sans-serif; color: #1D252D;";
    const cell = (text: string, firstRow: boolean, firstCol: boolean) =>
      `<td valign="top" style="width: 233.75pt; border: solid #000000 1pt;${
        firstRow ? '' : ' border-top: none;'
      }${firstCol ? '' : ' border-left: none;'} padding: 0cm 5.4pt;"><p><span style="${base}">${text}</span></p></td>`;
    const p = (text: string) => `<p><span style="${base}">${text}</span></p>`;
    return [
      '<!DOCTYPE html>',
      '<html>',
      `<body style="${base}">`,
      p(line('email.apple_google_pay.greeting', 'Hi,')),
      p('&nbsp;'),
      p(
        line(
          'email.apple_google_pay.activation_request',
          'Can you please activate the below customer’s apple/google pay token?',
        ),
      ),
      p('&nbsp;'),
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      `<tr>${cell(line('email.apple_google_pay.label_customer_name', 'Customer Name'), true, true)}${cell(name, true, false)}</tr>`,
      `<tr>${cell(line('email.apple_google_pay.label_vsdr_number', 'VSDR Number'), false, true)}${cell(card, false, false)}</tr>`,
      '</table>',
      p('&nbsp;'),
      p('&nbsp;'),
      '<table border="0" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">',
      `<tr>${cell(line('email.apple_google_pay.q_identified', 'Has the customer been securely identified?'), true, true)}${cell(line('email.apple_google_pay.yes_no', 'Yes/No'), true, false)}</tr>`,
      `<tr>${cell(line('email.apple_google_pay.q_extra_security', 'Has the customer passed Extra Security?'), false, true)}${cell(line('email.apple_google_pay.yes_no', 'Yes/No'), false, false)}</tr>`,
      `<tr>${cell(line('email.apple_google_pay.q_mobile_amended', 'Has the customer amended their mobile number in the past 72 hours? (16 screen)'), false, true)}${cell(line('email.apple_google_pay.yes_no', 'Yes/No'), false, false)}</tr>`,
      '</table>',
      p('&nbsp;'),
      p(line('email.apple_google_pay.sign_off', 'Kind Regards,')),
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * "EDS Data Breach" — the Everyday Digital Support data-breach draft the agent
 * completes when a customer (or a third party) reports that PTSB correspondence
 * (a letter, a card, …) has reached the wrong recipient. It carries the three
 * breach-scenario sections (Impacted Customer Called / 3rd Party Called –
 * Opened Post / 3rd Party Called – Did not open post) and the Social Media Post
 * guidance verbatim from the source template; the agent deletes the sections
 * that do not apply and fills in the event detail on the call. Nothing is
 * actioned automatically — the draft is addressed to the EDS admin mailbox
 * (#842). Prefilled best-effort from the in-context customer CIF (used in the
 * subject in place of the source template's `XXXXX` placeholder); every other
 * field is left blank / with the source template's own placeholder for the
 * agent to complete.
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml`, which reproduces the source draft's layout exactly (#842, see the
 * note on `buildHtml`); the plain-text `build` (line-for-line the source draft's
 * plain-text body) is retained for the subject and as the `mailto:` fallback.
 * Source: Drive "SCRN 23, 24 & 39 / EVERYDAY DIGITAL SUPPORT Data Breach.msg".
 */
export const EDS_DATA_BREACH: EmailTemplate = {
  id: 'eds-data-breach',
  label: 'EDS Data Breach',
  // #842 — the source Outlook draft is addressed to the EDS admin mailbox, CC'ing
  // the agent's team leader (a literal placeholder in the draft — there is no
  // fixed SMTP address for it). `to`/`cc` flow through to the `.eml` To:/Cc:
  // headers via `buildEml`, so the generated draft no longer opens with a blank
  // To line (@dylan219 confirmed, #842).
  to: 'EDSAdmin@PTSB.ie',
  cc: '**YOUR TEAM LEADER**',
  // #637 inc 30 — the editable prose of the EDS Data Breach draft now resolves from
  // the content catalogue; each default is byte-identical to the previous compiled-in
  // text, so with no override the output is unchanged. The subject's CIF
  // interpolation, the blank spacer lines and the `**`/`*\t` markers stay fixed in
  // code (text only, never logic).
  build: ({ cif = '', content }) => {
    const line = (key: string, def: string): string => content?.[key] ?? def;
    return {
      subject: `Data Breach - CIF: ${cif || 'XXXXX'}`,
      body: [
        ' ',
        line('email.eds_data_breach.title', 'Data Breach'),
        ' ',
        line(
          'email.eds_data_breach.menu_impacted_customer',
          'Impacted Customer Called',
        ),
        line('email.eds_data_breach.menu_three_types', '3 Types of Breaches'),
        line(
          'email.eds_data_breach.menu_opened_post',
          '3rd Party Called – Opened Post',
        ),
        line(
          'email.eds_data_breach.menu_unopened_post',
          '3rd Party Called – Unopened Post',
        ),
        line('email.eds_data_breach.menu_social_media', 'Social Media Post'),
        line(
          'email.eds_data_breach.menu_fill_draft',
          'Please fill in relevant draft',
        ),
        line(
          'email.eds_data_breach.delete_sections',
          '**Delete unnecessary sections**',
        ),
        ' ',
        line('email.eds_data_breach.ic_header', 'Impacted Customer Called'),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_event_description',
          'Please provide detailed event description and confirm what exact correspondence is affected:',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_date_time_call',
          'Date and Time of Call:',
        ),
        '\t',
        '',
        line(
          'email.eds_data_breach.ic_date_time_correspondence',
          'Date & Time correspondence/ card was issued?',
        ),
        ' ',
        '',
        line('email.eds_data_breach.ic_cif', 'Impacted Customer’s CIF:'),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_account_card',
          'Impacted Account and Card number(s):',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_visa_activated',
          'If Visa Credit Card was it previously activated?',
        ),
        '\t',
        '',
        line(
          'email.eds_data_breach.ic_correct_address',
          'At the time of this breach was the correct address on file?',
        ),
        '\t',
        '',
        line(
          'email.eds_data_breach.ic_lost_access',
          'If no, how long ago did customer lose access to post in old address?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_address_updated',
          'Was the address updated recently? And when was it updated (16 screen/ IAED)',
        ),
        '\t',
        '',
        line(
          'email.eds_data_breach.ic_address_now_updated',
          'If no, has the address now been updated, if not, why?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_retrieve_post',
          'Does Customer have access to retrieve post from the address it was issued to? If yes, how? Family Home, friend or relative live there still, etc.',
        ),
        line(
          'email.eds_data_breach.ic_must_be_asked',
          '***CUSTOMER MUST BE ASKED***',
        ),
        '\t',
        '',
        line(
          'email.eds_data_breach.ic_holds',
          'Any holds on the accounts (34 Screen)',
        ),
        line('email.eds_data_breach.ic_name_holds', '**Please name holds'),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_postal_address',
          'Any postal address (35 Screen)',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_retrieve_postal',
          'Do they have access to the retrieve post from the postal address? If yes, how? Family Home, friend or relative live there still etc',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.ic_action_taken',
          'What action have you taken to contain / mitigate breach ? Examples: ',
        ),
        '*\t' +
          line(
            'email.eds_data_breach.ic_action_3hold',
            '3 hold placed on account',
          ),
        '*\t' +
          line(
            'email.eds_data_breach.ic_action_card_cancelled',
            'Missing card has been cancelled',
          ),
        '*\t' +
          line(
            'email.eds_data_breach.ic_action_changed_address',
            'Changed address',
          ),
        ' ',
        ' ',
        ' ',
        line(
          'email.eds_data_breach.op_header',
          '3rd Party Called – Opened Post',
        ),
        '',
        line(
          'email.eds_data_breach.op_date_time_call',
          'Date and Time of Call:',
        ),
        ' ',
        '',
        line('email.eds_data_breach.op_please_ask', 'PLEASE ASK CALLER'),
        '',
        line(
          'email.eds_data_breach.op_name_address',
          'What is the name and address on the letter?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.op_letter_related',
          'Can you advise what the letter related to ?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.op_letter_date',
          'What is the date on the letter?/When did you receive this letter?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.op_other_correspondence',
          'Have you received any other correspondence from PTSB for this customer?',
        ),
        ' ',
        line('email.eds_data_breach.op_if_so', 'If So:'),
        line('email.eds_data_breach.op_recall_when', 'Do you recall when?'),
        line(
          'email.eds_data_breach.op_recall_related',
          'Do you recall what this post was related to?',
        ),
        line(
          'email.eds_data_breach.op_dispose',
          'Did you dispose of this post?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.op_how_long',
          'How long have you been living at this address?',
        ),
        line(
          'email.eds_data_breach.op_ever_lived',
          'Did our customer ever live at the address?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.op_dispose_return',
          'Can you please dispose of the correspondence securely or return it to your nearest PTSB branch? ',
        ),
        line(
          'email.eds_data_breach.op_confirm_how',
          '*** please confirm HOW the caller will dispose of OR destroy correspondence i.e. shred, burn, return to branch etc*** ',
        ),
        ' ',
        '',
        ' ',
        line('email.eds_data_breach.op_agent_fill', 'AGENT TO FILL OUT'),
        '',
        line('email.eds_data_breach.op_source', 'Source of Breach:'),
        ' ',
        ' ',
        '',
        line('email.eds_data_breach.op_cif', 'Impacted Customer’s CIF'),
        ' ',
        '',
        line('email.eds_data_breach.op_time_period', 'Time Period Impacted '),
        ' ',
        ' ',
        '',
        line(
          'email.eds_data_breach.op_data_compromised',
          'The full amount and nature of the data that has been compromised?',
        ),
        ' ',
        ' ',
        '',
        line(
          'email.eds_data_breach.op_other_info',
          'Any other relevant information?',
        ),
        ' ',
        ' ',
        ' ',
        line(
          'email.eds_data_breach.up_header',
          '3rd Party Called – Did not open post',
        ),
        '',
        line(
          'email.eds_data_breach.up_date_time_call',
          'Date and Time of Call:',
        ),
        ' ',
        '',
        line('email.eds_data_breach.up_please_ask', 'PLEASE ASK CALLER'),
        '',
        line(
          'email.eds_data_breach.up_name_address',
          'What is the name and address on the letter?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.up_other_correspondence',
          'Have you received any other correspondence from PTSB for this customer?',
        ),
        ' ',
        line('email.eds_data_breach.up_if_so', 'If So:'),
        line('email.eds_data_breach.up_recall_when', 'Do you recall when?'),
        line(
          'email.eds_data_breach.up_recall_related',
          'Do you recall what this post was related to?',
        ),
        line(
          'email.eds_data_breach.up_dispose',
          'Did you dispose of this post?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.up_how_long',
          'How long have you been living at this address?',
        ),
        line(
          'email.eds_data_breach.up_ever_lived',
          'Did our customer ever live at the address?',
        ),
        ' ',
        '',
        line(
          'email.eds_data_breach.up_dispose_return',
          'Can you please dispose of the correspondence securely or return it to your nearest PTSB branch? ',
        ),
        line(
          'email.eds_data_breach.up_confirm_how',
          '*** please confirm HOW the caller will dispose of OR destroy correspondence i.e. shred, burn, return to branch***',
        ),
        ' ',
        '',
        line('email.eds_data_breach.up_agent_fill', 'AGENT TO FILL OUT'),
        '',
        line('email.eds_data_breach.up_source', 'Source of Breach:'),
        ' ',
        ' ',
        '',
        line('email.eds_data_breach.up_cif', 'Impacted Customer’s CIF'),
        ' ',
        '',
        line('email.eds_data_breach.up_time_period', 'Time Period Impacted:'),
        ' ',
        ' ',
        '',
        line(
          'email.eds_data_breach.up_data_compromised',
          'The full amount and nature of the data that has been compromised?',
        ),
        ' ',
        ' ',
        '',
        line(
          'email.eds_data_breach.up_other_info',
          'Any other relevant information?',
        ),
        ' ',
        ' ',
        ' ',
        line('email.eds_data_breach.sm_header', 'Social Media Post'),
        ' ',
        line(
          'email.eds_data_breach.sm_recommendation',
          'If we become aware over Social Media that correspondence has been received for a customer, our recommendation for the response should be:',
        ),
        ' ',
        line(
          'email.eds_data_breach.sm_response',
          '‘Hi XXX, Thank you for bringing this matter to our attention. In regard to the post you have received can we ask you to return to sender? Thanks, XXXX’ ',
        ),
        ' ',
        line(
          'email.eds_data_breach.sm_third_party',
          'If a third party provides details of the impacted customer (the customer whose Data has been impacted by this Breach) i.e.. name and address we should be following our BAU process with Data Breaches and fill out the above Data Breach Draft by looking the customers details up on Uniscreens.',
        ),
        ' ',
        ' ',
        '',
      ].join('\n'),
    };
  },
  // #842 (2026-10-20, @dylan219: "the format of the email body is an exact
  // match") — reproduces the source Outlook draft "SCRN 23, 24 & 39 / EVERYDAY
  // DIGITAL SUPPORT Data Breach.msg" markup-for-markup, with only Word-specific
  // junk dropped (mso-* properties, conditional comments, `o:p`, unused class
  // definitions). The previous render (!1202) simplified the layout into one
  // stylesheet-driven grid; this follows the source exactly:
  //  - Inter 12pt throughout (a `<style>` default mirroring Word's
  //    `p.MsoNormal` — margin 0); the title "Data Breach" bold + underlined 16pt,
  //    centred; the six breach-type menu lines bold, centred, PTSB orange
  //    (#FC4C02); "**Delete unnecessary sections**" bold, centred, red; the
  //    "3rd" ordinals superscripted.
  //  - "Impacted Customer Called", "3rd Party Called – Opened Post" and
  //    "3rd Party Called – Did not open post" each a bold orange heading above a
  //    two-column bordered table (1pt black borders, 247.7pt prompt column +
  //    ~496pt answer column, the source's row heights). Several prompts share a
  //    cell as separate paragraphs, exactly as in the source (e.g. "Any holds…"
  //    + "**Please name holds"; the "If So:" follow-ups); "PLEASE ASK CALLER" /
  //    "AGENT TO FILL OUT" are bold full-width (colspan=2) rows.
  //  - The Impacted-Customer prompts are slate (#1D242C) at 115% line height;
  //    "***CUSTOMER MUST BE ASKED***" red (not bold); the mitigation examples a
  //    disc-bulleted list inside the prompt cell; the "*** please confirm HOW…"
  //    advisories bold red with "HOW " underlined.
  //  - "Social Media Post" is paragraphs (not a table), the recommended
  //    response bold + italic + underlined orange with an italic opening quote.
  // Every piece of text resolves from the SAME content-catalogue keys (#637 inc
  // 30) and is HTML-escaped at render; formatting stays fixed in code. The body
  // carries no interpolated context (only the subject uses the CIF).
  buildHtml: ({ content }) => {
    const line = (key: string, def: string): string =>
      escapeHtml(content?.[key] ?? def);
    const k = (suffix: string, def: string): string =>
      line(`email.eds_data_breach.${suffix}`, def);
    // "3rd" → "3<sup>rd</sup>", as in the source headings.
    const ord = (html: string): string =>
      html.replace(/\b3rd\b/g, '3<sup>rd</sup>');
    const nb = '&nbsp;';
    const p = (html: string, style = ''): string =>
      `<p${style ? ` style="${style}"` : ''}>${html}</p>`;
    const span = (style: string, html: string): string =>
      `<span style="${style}">${html}</span>`;
    const orange = (html: string): string => span('color:#FC4C02', html);
    const centred = (html: string): string =>
      `<p align="center" style="text-align:center">${html}</p>`;
    const menu = (suffix: string, def: string): string =>
      centred(`<b>${orange(ord(k(suffix, def)))}</b>`);
    const heading = (suffix: string, def: string): string =>
      p(`<b>${orange(ord(k(suffix, def)))}</b>`);
    // The "*** please confirm HOW …" advisory: bold red, "HOW " underlined.
    const confirmHow = (suffix: string, def: string): string => {
      const text = k(suffix, def);
      const red = (html: string): string => `<b>${span('color:red', html)}</b>`;
      const lead = text.startsWith('*** ');
      const rest = (lead ? text.slice(4) : text).replace('HOW ', '<u>HOW </u>');
      return p(lead ? `${red('***')} ${red(rest)}` : red(rest));
    };

    // One two-column table. `rows` = [prompt paragraphs | 'full-width' html,
    // row height]; the first row carries the table's top border.
    type Row = { cell: string; full?: boolean; h?: string };
    const table = (
      rightW: [number, string],
      fullW: [number, string],
      rows: Row[],
    ): string => {
      const B = 'solid windowtext 1.0pt';
      const pad = 'padding:0cm 5.4pt';
      const body = rows
        .map((r, i) => {
          const top = i === 0 ? '' : ';border-top:none';
          const h = r.h ? `;height:${r.h}` : '';
          const tr = r.h ? `<tr style="height:${r.h}">` : '<tr>';
          if (r.full) {
            return `${tr}<td width="${fullW[0]}" colspan="2" valign="top" style="width:${fullW[1]};border:${B}${top};${pad}${h}">${r.cell}</td></tr>`;
          }
          return (
            `${tr}<td width="330" valign="top" style="width:247.7pt;border:${B}${top};${pad}${h}">${r.cell}</td>` +
            `<td width="${rightW[0]}" valign="top" style="width:${rightW[1]};border:${B}${i === 0 ? '' : ';border-top:none'};border-left:none;${pad}${h}">${p(nb)}</td></tr>`
          );
        })
        .join('');
      return `<table border="0" cellspacing="0" cellpadding="0" style="border-collapse:collapse">${body}</table>`;
    };

    // Impacted Customer Called — slate prompts at 115% line height.
    const ic = (suffix: string, def: string): string =>
      p(span('color:#1D242C', k(suffix, def)), 'line-height:115%');
    const icRow = (suffix: string, def: string, h?: string): Row => ({
      cell: ic(suffix, def),
      h,
    });
    const li = (suffix: string, def: string): string =>
      `<li style="color:#1D242C;margin-left:0cm;line-height:115%">${k(suffix, def)}</li>`;
    const icTable = table(
      [661, '496.1pt'],
      [991, '743.8pt'],
      [
        icRow(
          'ic_event_description',
          'Please provide detailed event description and confirm what exact correspondence is affected:',
        ),
        icRow('ic_date_time_call', 'Date and Time of Call:'),
        icRow(
          'ic_date_time_correspondence',
          'Date & Time correspondence/ card was issued?',
        ),
        icRow('ic_cif', 'Impacted Customer’s CIF:'),
        icRow('ic_account_card', 'Impacted Account and Card number(s):'),
        icRow(
          'ic_visa_activated',
          'If Visa Credit Card was it previously activated?',
        ),
        icRow(
          'ic_correct_address',
          'At the time of this breach was the correct address on file?',
          '26.35pt',
        ),
        icRow(
          'ic_lost_access',
          'If no, how long ago did customer lose access to post in old address?',
          '26.35pt',
        ),
        icRow(
          'ic_address_updated',
          'Was the address updated recently? And when was it updated (16 screen/ IAED)',
          '22.6pt',
        ),
        icRow(
          'ic_address_now_updated',
          'If no, has the address now been updated, if not, why?',
          '22.6pt',
        ),
        {
          cell:
            ic(
              'ic_retrieve_post',
              'Does Customer have access to retrieve post from the address it was issued to? If yes, how? Family Home, friend or relative live there still, etc.',
            ) +
            p(
              span(
                'color:red',
                k('ic_must_be_asked', '***CUSTOMER MUST BE ASKED***'),
              ),
              'line-height:115%',
            ),
        },
        {
          cell:
            ic('ic_holds', 'Any holds on the accounts (34 Screen)') +
            ic('ic_name_holds', '**Please name holds'),
          h: '27.85pt',
        },
        icRow('ic_postal_address', 'Any postal address (35 Screen)', '27.85pt'),
        icRow(
          'ic_retrieve_postal',
          'Do they have access to the retrieve post from the postal address? If yes, how? Family Home, friend or relative live there still etc',
          '27.85pt',
        ),
        {
          cell:
            ic(
              'ic_action_taken',
              'What action have you taken to contain / mitigate breach ? Examples: ',
            ) +
            '<ul type="disc" style="margin-top:0cm;margin-bottom:0cm">' +
            li('ic_action_3hold', '3 hold placed on account') +
            li('ic_action_card_cancelled', 'Missing card has been cancelled') +
            li('ic_action_changed_address', 'Changed address') +
            '</ul>',
          h: '27.85pt',
        },
      ],
    );

    // The two 3rd-party sections share a shape (default-black prompts).
    const prompts = (...html: string[]): string =>
      html.map((h) => p(h)).join('');
    const third = (s: 'op' | 'up'): string => {
      const opened = s === 'op';
      const q = (suffix: string, def: string): string =>
        k(`${s}_${suffix}`, def);
      const rows: Row[] = [
        { cell: prompts(q('date_time_call', 'Date and Time of Call:')) },
        {
          cell: p(`<b>${q('please_ask', 'PLEASE ASK CALLER')}</b>`),
          full: true,
        },
        {
          cell: prompts(
            q('name_address', 'What is the name and address on the letter?'),
          ),
          h: '29.55pt',
        },
      ];
      if (opened) {
        rows.push(
          {
            cell: prompts(
              q(
                'letter_related',
                'Can you advise what the letter related to ?',
              ),
            ),
            h: '29.55pt',
          },
          {
            cell: prompts(
              q(
                'letter_date',
                'What is the date on the letter?/When did you receive this letter?',
              ),
            ),
            h: '29.55pt',
          },
        );
      }
      rows.push(
        {
          cell: prompts(
            q(
              'other_correspondence',
              'Have you received any other correspondence from PTSB for this customer?',
            ),
            nb,
            q('if_so', 'If So:'),
            q('recall_when', 'Do you recall when?'),
            q('recall_related', 'Do you recall what this post was related to?'),
            q('dispose', 'Did you dispose of this post?'),
          ),
          h: '29.55pt',
        },
        {
          cell: prompts(
            q('how_long', 'How long have you been living at this address?'),
            q('ever_lived', 'Did our customer ever live at the address?'),
          ),
          h: '29.55pt',
        },
        {
          cell:
            p(
              q(
                'dispose_return',
                'Can you please dispose of the correspondence securely or return it to your nearest PTSB branch? ',
              ),
            ) +
            confirmHow(
              `${s}_confirm_how`,
              opened
                ? '*** please confirm HOW the caller will dispose of OR destroy correspondence i.e. shred, burn, return to branch etc*** '
                : '*** please confirm HOW the caller will dispose of OR destroy correspondence i.e. shred, burn, return to branch***',
            ),
          h: opened ? '47.9pt' : '29.55pt',
        },
        {
          cell:
            (opened ? p(`<b>${nb}</b>`) : '') +
            p(`<b>${q('agent_fill', 'AGENT TO FILL OUT')}</b>`),
          full: true,
          h: '29.55pt',
        },
        {
          cell: prompts(q('source', 'Source of Breach:'), nb),
          h: '27.85pt',
        },
        { cell: prompts(q('cif', 'Impacted Customer’s CIF')), h: '28.25pt' },
        {
          cell: prompts(
            q(
              'time_period',
              opened ? 'Time Period Impacted ' : 'Time Period Impacted:',
            ),
            nb,
          ),
          h: '28.25pt',
        },
        {
          cell: prompts(
            q(
              'data_compromised',
              'The full amount and nature of the data that has been compromised?',
            ),
            nb,
          ),
        },
        { cell: prompts(q('other_info', 'Any other relevant information?')) },
      );
      return table([662, '496.25pt'], [992, '743.95pt'], rows);
    };

    const smResponse = k(
      'sm_response',
      '‘Hi XXX, Thank you for bringing this matter to our attention. In regard to the post you have received can we ask you to return to sender? Thanks, XXXX’ ',
    );
    // The source sets the opening quote italic only; the rest bold + italic +
    // underlined, all orange.
    const quote = smResponse.startsWith('‘')
      ? `<i>${orange('‘')}</i><b><i><u>${orange(smResponse.slice(1))}</u></i></b>`
      : `<b><i><u>${orange(smResponse)}</u></i></b>`;

    return [
      '<!DOCTYPE html>',
      '<html>',
      '<head>',
      `<style>p,li{margin:0cm;font-size:12.0pt;font-family:'Inter',serif;}ul{margin-bottom:0cm;}</style>`,
      '</head>',
      '<body lang="EN-IE" link="#FB4C02" vlink="#1D242B">',
      p(nb, "font-size:11.0pt;font-family:'Calibri',sans-serif"),
      centred(
        `<b><u>${span('font-size:16.0pt', k('title', 'Data Breach'))}</u></b>`,
      ),
      centred(`<b>${span('color:#E46C0A', nb)}</b>`),
      menu('menu_impacted_customer', 'Impacted Customer Called'),
      menu('menu_three_types', '3 Types of Breaches'),
      menu('menu_opened_post', '3rd Party Called – Opened Post'),
      menu('menu_unopened_post', '3rd Party Called – Unopened Post'),
      menu('menu_social_media', 'Social Media Post'),
      menu('menu_fill_draft', 'Please fill in relevant draft'),
      centred(
        `<b>${span('color:red', k('delete_sections', '**Delete unnecessary sections**'))}</b>`,
      ),
      p(orange(nb)),
      heading('ic_header', 'Impacted Customer Called'),
      p(`<b>${span('color:#E46C0A', nb)}</b>`),
      icTable,
      p(nb),
      p(nb),
      heading('op_header', '3rd Party Called – Opened Post'),
      third('op'),
      p(nb),
      p(nb),
      heading('up_header', '3rd Party Called – Did not open post'),
      third('up'),
      p(`<b>${span('color:#E46C0A', nb)}</b>`),
      p(`<b>${span('color:#E46C0A', nb)}</b>`),
      heading('sm_header', 'Social Media Post'),
      p(nb),
      p(
        k(
          'sm_recommendation',
          'If we become aware over Social Media that correspondence has been received for a customer, our recommendation for the response should be:',
        ),
      ),
      p(nb),
      p(quote),
      p(nb),
      p(
        k(
          'sm_third_party',
          'If a third party provides details of the impacted customer (the customer whose Data has been impacted by this Breach) i.e.. name and address we should be following our BAU process with Data Breaches and fill out the above Data Breach Draft by looking the customers details up on Uniscreens.',
        ),
      ),
      p(nb),
      p(nb),
      '</body>',
      '</html>',
    ].join('\n');
  },
};

/**
 * Default wording of the shared Data Breach Notification body (Everyday Lending /
 * Business Banking / Mortgage), keyed by the content-catalogue key suffix
 * (`email.data_breach_notification.<key>`, #637 inc 27). Shared by the plain-text
 * body and the HTML `.eml` body (#842) so both resolve the same wording.
 */
const DATA_BREACH_NOTIFICATION_TEXT = {
  heading: 'Data Breach Notification Template',
  s1_header: 'Section 1: MANDATORY for ALL Data Breach Notifications',
  date_breach_identified: 'Date Breach Identified:',
  late_notification:
    'If you are notifying the Data Protection Team outside 24 hours, provide responsible unit for late notification & reasons for delay:',
  how_became_aware: 'How did you become aware of the data breach?',
  how_became_aware_hint:
    '(E.G.  notification by data subject, notification by third party, system auditing/testing,  Internal employee review)',
  complaint_logged: 'Has a complaint been logged?',
  complaint_logged_hint: '(Yes/No – if Yes, enter CMS no)',
  list_data_subjects:
    'List all data subjects that have been impacted (i.e. all names on correspondence): ',
  list_data_subjects_hint: '(enter Customer Names & Associated CIF’s)',
  list_account_numbers: 'List impacted account numbers:',
  list_account_numbers_hint: '(List ALL impacted account Numbers)',
  vulnerable_or_special:
    'Are there any vulnerable data subjects impacted or special categories of data disclosed?',
  vulnerable_or_special_hint: '(refer to the Data Security Breach Procedure)',
  complete_one_header:
    '****Please also complete ONE of the following sections: ',
  route_section2:
    'If the data breach relates to an External, An Post issue – Complete SECTION 2',
  route_section3:
    'If the data breach relates to Inaccurate Customer Data (i.e. customer failed to update address – Complete SECTION 3)',
  route_section4:
    'If the data breach occurred as a result of Human Error and you have not yet submitted a Risk Event on the GRC system – Complete SECTION 4)',
  route_section5:
    'If the data breach is a CCR impacting issue – Complete SECTION 5)',
  delete_unused_sections:
    'You can delete any sections not relevant to this breach when submitting to the Data Protection team',
  s2_header:
    'Section 2: EXTERNAL POSTAL ISSUES (i.e. address is correct, but correspondence not delivered) ',
  s2_address_accurate: 'Confirm that the address is accurate?',
  s2_address_accurate_hint: '(Yes/No – if No, go to Section 3)',
  s2_correspondence_not_received:
    'What correspondence was not received and has not been returned to the Bank?',
  s2_correspondence_not_received_hint:
    '(E.G. Visa debit card, Visa Credit Card, Mortgage Statements, Current a/c statement, Mtg Options letter, Arrears letter).  ',
  s2_attach_copy:
    'If not listed above, attach a copy of the correspondence that was not received',
  s2_correspondence_date: 'What date did the correspondence issue?',
  s2_immediate_action: 'Immediate action taken to contain/mitigate breach:',
  s2_immediate_action_hint:
    '(i.e. (a) if debit or credit cards-, has the card been cancelled and not activated ;  (b) has the correspondence been re-issued ;  (c) has an Eircode been added to the address to assist delivery) etc.',
  s3_header:
    'Section 3: INACCURATE CUSTOMER DATA  (i.e. address is incorrect; customer failed to notify of change of address)',
  s3_how_long_moved:
    'How long has the customer(s) not lived at the address on our records? Specify when they moved.',
  s3_listed_all_accounts:
    'Have you listed ALL accounts that the customer holds with the Bank in Section 1?',
  s3_listed_all_accounts_hint:
    'Yes/No -  if No complete Section 1 with all associated account numbers (this could include former account numbers if correspondence issued during the impacted period of the data breach)',
  s3_returned_post: 'Is there a record of Returned Post?',
  s3_returned_post_hint:
    'Check Imaging /Uniscreens history to see if any returned post',
  s3_list_all_correspondence:
    'List ALL correspondence (that has not been returned) for ALL accounts that issued, since customer moved?',
  s3_address_updated: 'Has the address been updated?',
  s3_address_updated_ifno:
    'If NO, have steps been taken to prevent further correspondence issuing to the incorrect address?',
  s3_yesno: 'Yes/No ',
  s3_ifno_measures:
    'If NO, what organisational and technical measures have been taken?  I.e. contacted customer to seek updated address details, placed postal holds on account(s), place C/O address (required in certain circumstances) ',
  s3_secured_restored: 'Have you secured/restored the breached data? ',
  s3_secured_restored_ifnot:
    'If not, confirm the date you have issued a letter to the Householder to secure/retrieve correspondence?',
  s3_householder_letter_hint:
    '(For all correspondence except for cards (that have been cancelled), a letter should issue to the Householder of the former address, requesting they RETURN (if correspondence issued in past month) or DISREGARD and DESTROY unless this has been confirmed by a third party recipient. ',
  s3_template_letters: 'Template letters are available. ',
  s3_other_actions:
    'What other actions have been taken to contain/mitigate the data breach:',
  s4_header:
    'Section 4: HUMAN/BANK ERROR  (Complete this section if you have not submitted a Risk Event on GRC system already)',
  s4_ccr_redirect: 'If there is a CCR Impact- Instead complete Section 5. ',
  s4_responsible_unit: 'Who is the responsible business unit?',
  s4_responsible_unit_hint:
    'Ensure the responsible business unit has been cc’d and is aware of the data breach so they can investigate',
  s4_description:
    'Provide a detailed description of the data breach, including the nature of the personal data compromised. Provide as much detail as possible as to what has occurred and the human error that led to the data breach?',
  s4_description_hint:
    '(this section should be completed for any data breaches that have arisen from human error such as data entry errors,  e-mail errors, envelope errors, unauthorised verbal disclosure, loss of documentation, mailing error,  system (I.T. errors) that has led to unauthorised disclosure,  loss or unauthorised access to personal data)',
  s4_listed_all:
    'Have you listed ALL accounts/data subjects impacted by the data breach in Section 1?',
  s4_listed_all_hint:
    'Yes/No -  if No complete Section 1 with all associated account numbers (this could include former account numbers if correspondence issued during the impacted period of the data breach) and data subjects',
  s4_list_correspondence:
    'List ALL correspondence /personal data  impacted by this data breach:',
  s4_secured_restored:
    'Have you secured/restored the breached data? If not, confirm the date you have issued a letter to the Householder to secure/retrieve correspondence?',
  s4_householder_letter_hint:
    '(For all correspondence except for cards (that have been cancelled), a letter should issue to the Householder of the former address, requesting they RETURN (if correspondence issued in past month) or DISREGARD and DESTROY unless this has been confirmed by a third party recipient. ',
  s4_template_letters: 'Template letters are available.',
  s4_measures_before:
    'What measures/controls were in place prior to the incident occurring',
  s4_measures_before_hint:
    '(E.G. 4 eye checks, what is outlined in procedure guides, QA, staff training etc.)',
  s4_deficiencies:
    'What deficiencies have you identified as a result of this data breach?',
  s4_deficiencies_hint:
    '(E.G. Failure to follow procedure,  failure to act on customer’s instruction,  inaction by staff member)',
  s4_measures_now:
    'What measures/controls have you now put in place to prevent this incident re-occurring',
  s4_measures_now_hint:
    '(E.G. introduced 4 eye checks, updated procedure guides, increased % on QA, staff training & feedback to staff member etc.)',
  s4_consequences:
    'What consequences has this data breach had on the data subject? ',
  s4_consequences_hint:
    '(E.G. financial loss,  fraud, damage to reputation,  loss of control over their personal data)',
  s4_customer_aware: 'Is the customer aware that a data breach has occurred?',
  s4_customer_aware_ifyes:
    'If YES, how was this communicated and what was the content of the communication? ',
  s4_customer_aware_yesno:
    'Yes/No – if No (the Data Protection Team will review and advise if a communication to the impacted data subject is required)',
  s4_customer_aware_ifyes_detail:
    'If Yes – how was the data subject informed – telephone call, in writing, in person (branch). What were they advised in relation to the data breach',
  s5_header:
    'Section 5: CCR Impacting Errors (i.e. CCR Mapping issue or Human/Process error that led to misreporting to CCR) ',
  s5_responsible_unit: 'Who is the responsible business unit?',
  s5_responsible_unit_hint:
    'Ensure the responsible business unit has been cc’d and is aware of the data breach so they can investigate',
  s5_description:
    'Provide a detailed description of the data breach including what product has been impacted.  Provide as much detail as possible as to what has occurred which led to this misreporting to the CCR?',
  s5_description_hint1:
    '(E.G Does this impact a credit card, overdraft,  term loan, mortgage) ',
  s5_description_hint2:
    'Is this a CCR Mapping issue, agent failed to close account which has resulted in misreporting, Failure to backdate CCR, gap in Bank process etc.)',
  s5_known_cie:
    'Is this a known CCR misreporting issue subject to an existing CIE?',
  s5_single_multiple:
    'Is this a single/ multiple customer impacting error? If multiple, how many impacted?',
  s5_known_cie_hint:
    'Yes/No - if Yes reference the CIE (e.g. Misreporting of authorised signatories, Revoked credit card etc.)',
  s5_consider_more:
    'Consider, is it possible that this error could impact more customers – or is this a once off error?',
  s5_how_long:
    'How long has the CCR been misreporting? (Specify the impacted time period)',
  s5_how_long_hint: '(e.g.  Jan 2021 – Aug 2022)',
  s5_prior_arrears:
    'Has the customer previously been in arrears within the past 5 years with a legitimate arrears history showing on the CCR?',
  s5_prior_arrears_hint: 'Yes/No – if Yes provide details',
  s5_account_closed:
    'If the account should have been closed, give details on when the account was paid in full?',
  s5_account_closed_hint:
    '(E.g. provide the dates on when the account should have been closed, when the account was paid in full /written off / settled for less etc.) ',
  s5_actions_taken:
    'What actions have you taken to contain/mitigate the data breach?',
  s5_action_a:
    'a.\tHas the account now been closed? (if applicable)/Confirm a request has been sent to close account?',
  s5_action_b:
    'b.\tHas an amendment/deletion request been sent to CCR? Confirm when this will be sent?',
  s5_measures_before:
    'What measures/controls were in place prior to the incident occurring',
  s5_measures_before_hint:
    '(E.G. what is outlined in procedure guides, staff training)',
  s5_deficiencies:
    'What deficiencies have you identified as a result of this data breach?',
  s5_deficiencies_hint:
    '(E.G. CCR Mapping issue,  failure to follow procedure,  failure to act on customer’s instruction,  inaction by staff member)',
  s5_measures_now:
    'What measures/controls have you now put in place to prevent this incident re-occurring',
  s5_measures_now_hint:
    '(E.G. introduced 4 eye checks, updated  procedure guides, increased % on QA, staff training & feedback to staff member,  CCR fix etc.)',
  s5_consequences:
    'What consequences has this data breach had on the data subject? ',
  s5_consequences_hint:
    '(E.G. has this customer been declined lending as a result of this misreporting?)',
  s5_customer_aware:
    'Is the customer aware that their CCR has been impacted by misreporting error?',
  s5_customer_aware_ifyes:
    'If YES, how was this communicated and what was the content of the communication?',
  s5_customer_aware_yesno:
    'Enter Yes/No – if No (the Data Protection Team will review and advise if a communication to the impacted data subject is required)',
  s5_customer_aware_ifyes_detail:
    'If Yes – how was the data subject informed – telephone call, in writing, in person (branch). What were they advised in relation to the data breach',
  dp_team: 'Data Protection Team ',
  dp_reference: 'DP Reference: ',
  comments: 'Comments/Recommendations:',
} as const;

type DataBreachNotificationKey = keyof typeof DATA_BREACH_NOTIFICATION_TEXT;

/** Resolves one line of the shared notification body (catalogue override, else default). */
function dataBreachNotificationLine(
  key: DataBreachNotificationKey,
  content?: Readonly<Record<string, string>>,
): string {
  return (
    content?.[`email.data_breach_notification.${key}`] ??
    DATA_BREACH_NOTIFICATION_TEXT[key]
  );
}

/**
 * The shared "Data Breach Notification Template" body used verbatim by the
 * product-specific Data Breach drafts (Everyday Lending / Business Banking /
 * Mortgage). The three source `.msg` files carry an identical five-section
 * notification body (Section 1 MANDATORY + Sections 2–5, one of which the agent
 * completes) and differ only in their subject prefix, so the body is factored
 * here and reproduced verbatim from the source templates — smart quotes and
 * section spacing preserved. The agent deletes the sections that do not apply
 * and completes the event detail on the call before sending to the Data
 * Protection Team.
 * Source: Drive "SCRN 23, 24 & 39 / {EVERYDAY LENDING|BUSINESS BANKING|MORTGAGE}
 * Data Breach.msg".
 */
function dataBreachNotificationBody(
  content?: Readonly<Record<string, string>>,
): string {
  // #637 inc 27 — resolve the editable prose of the shared Data Breach
  // Notification body (Everyday Lending / Business Banking / Mortgage) from the
  // content catalogue; each default is byte-identical to the previous
  // compiled-in text, so with no override the output is unchanged. The
  // blank/whitespace-only spacer lines are layout, not editable text, and stay
  // fixed in code (text only, never logic).
  const t = (key: DataBreachNotificationKey): string =>
    dataBreachNotificationLine(key, content);
  return [
    ' ',
    t('heading'),
    '',
    t('s1_header'),
    ' ',
    ' ',
    '',
    t('date_breach_identified'),
    ' ',
    '',
    t('late_notification'),
    ' ',
    '',
    t('how_became_aware'),
    t('how_became_aware_hint'),
    '',
    t('complaint_logged'),
    t('complaint_logged_hint'),
    '',
    t('list_data_subjects'),
    t('list_data_subjects_hint'),
    ' ',
    '',
    t('list_account_numbers'),
    t('list_account_numbers_hint'),
    '',
    t('vulnerable_or_special'),
    t('vulnerable_or_special_hint'),
    '',
    t('complete_one_header'),
    t('route_section2'),
    t('route_section3'),
    t('route_section4'),
    t('route_section5'),
    t('delete_unused_sections'),
    ' ',
    '',
    t('s2_header'),
    '\t',
    '',
    t('s2_address_accurate'),
    t('s2_address_accurate_hint'),
    '',
    t('s2_correspondence_not_received'),
    t('s2_correspondence_not_received_hint'),
    t('s2_attach_copy'),
    '',
    t('s2_correspondence_date'),
    ' ',
    '',
    t('s2_immediate_action'),
    t('s2_immediate_action_hint'),
    ' ',
    '\t\t\t',
    ' ',
    '',
    t('s3_header'),
    '\t',
    '',
    t('s3_how_long_moved'),
    ' ',
    '',
    t('s3_listed_all_accounts'),
    t('s3_listed_all_accounts_hint'),
    '',
    t('s3_returned_post'),
    t('s3_returned_post_hint'),
    '',
    t('s3_list_all_correspondence'),
    ' ',
    '',
    t('s3_address_updated'),
    t('s3_address_updated_ifno'),
    t('s3_yesno'),
    t('s3_ifno_measures'),
    '',
    t('s3_secured_restored'),
    t('s3_secured_restored_ifnot'),
    t('s3_householder_letter_hint'),
    t('s3_template_letters'),
    '',
    t('s3_other_actions'),
    ' ',
    ' ',
    ' ',
    ' ',
    ' ',
    '\t\t\t',
    ' ',
    '',
    t('s4_header'),
    t('s4_ccr_redirect'),
    '\t',
    '',
    t('s4_responsible_unit'),
    t('s4_responsible_unit_hint'),
    '',
    t('s4_description'),
    t('s4_description_hint'),
    '',
    t('s4_listed_all'),
    t('s4_listed_all_hint'),
    '',
    t('s4_list_correspondence'),
    ' ',
    ' ',
    '',
    t('s4_secured_restored'),
    t('s4_householder_letter_hint'),
    t('s4_template_letters'),
    '',
    t('s4_measures_before'),
    t('s4_measures_before_hint'),
    '',
    t('s4_deficiencies'),
    t('s4_deficiencies_hint'),
    '',
    t('s4_measures_now'),
    t('s4_measures_now_hint'),
    '',
    t('s4_consequences'),
    t('s4_consequences_hint'),
    '',
    t('s4_customer_aware'),
    t('s4_customer_aware_ifyes'),
    t('s4_customer_aware_yesno'),
    t('s4_customer_aware_ifyes_detail'),
    '\t\t\t',
    ' ',
    '',
    t('s5_header'),
    ' ',
    '',
    t('s5_responsible_unit'),
    t('s5_responsible_unit_hint'),
    '',
    t('s5_description'),
    t('s5_description_hint1'),
    t('s5_description_hint2'),
    '',
    t('s5_known_cie'),
    t('s5_single_multiple'),
    t('s5_known_cie_hint'),
    ' ',
    t('s5_consider_more'),
    '',
    t('s5_how_long'),
    t('s5_how_long_hint'),
    '',
    t('s5_prior_arrears'),
    t('s5_prior_arrears_hint'),
    '',
    t('s5_account_closed'),
    t('s5_account_closed_hint'),
    '',
    t('s5_actions_taken'),
    t('s5_action_a'),
    t('s5_action_b'),
    '',
    t('s5_measures_before'),
    t('s5_measures_before_hint'),
    '',
    t('s5_deficiencies'),
    t('s5_deficiencies_hint'),
    '',
    t('s5_measures_now'),
    t('s5_measures_now_hint'),
    '',
    t('s5_consequences'),
    t('s5_consequences_hint'),
    '',
    t('s5_customer_aware'),
    t('s5_customer_aware_ifyes'),
    t('s5_customer_aware_yesno'),
    t('s5_customer_aware_ifyes_detail'),
    ' ',
    ' ',
    '',
    t('dp_team'),
    '',
    t('dp_reference'),
    ' ',
    '',
    t('comments'),
    ' ',
    ' ',
    '',
  ].join('\n');
}

/**
 * #842 (2026-10-20, @dylan219: "the format of the email body is an exact match")
 * — the shared Data Breach Notification body rendered as the `.eml` HTML for the
 * product-specific Data Breach drafts (Everyday Lending / Business Banking /
 * Mortgage). The three source Outlook drafts "SCRN 23, 24 & 39 / {EVERYDAY
 * LENDING|BUSINESS BANKING|MORTGAGE} Data Breach.msg" carry a byte-identical body
 * (they differ only in the subject prefix), so this renders it once.
 *
 * It reproduces the source markup table-for-table, with only Word-specific junk
 * dropped (mso-* properties, VML, conditional comments, `o:p`, unused classes).
 * The previous render (!1202) re-shaped the content into a generic stylesheet
 * grid (Inter 12pt slate, grey borders, accent-bar headings) — this follows the
 * source exactly:
 *  - Calibri 11pt black body (a `<style>` default mirroring Word's
 *    `p.MsoNormal`); the title "Data Breach Notification Template" bold 24pt
 *    green (#70AD47).
 *  - Each Section opens with a full-width header bar of bold white text — Section
 *    1 and the closing "Data Protection Team" bar on dark red (#C0504D), Sections
 *    2–5 on slate (#44546A) — with the source's orange (#ED7D31) top/left/right
 *    rule; "ALL " (Section 1) and "have not" (Section 4) underlined; Section 4's
 *    bar carries its second "If there is a CCR Impact…" line.
 *  - Each Section's questions are a two-column table: the question (black) on
 *    the left, its guidance on the right in grey 9pt italics; 1.5pt black outer
 *    border, 1pt inner rules; the source's column widths and 19.15pt row heights.
 *    Multi-line questions/guidance stay separate paragraphs in one cell; the
 *    source's bold/underlined words (ALL, detailed, prior, now, NO, YES) are kept.
 *  - Section 1's "****Please also complete ONE of the following sections" block
 *    is a full-width row of 9pt italics with the four breach types in bold red;
 *    Section 5's two follow-up actions are an a./b. list in grey 9pt italics.
 *  - The "Data Protection Team" block is a table of bold-italic-underlined labels
 *    ("DP Reference:", "Comments/Recommendations:") with blank answer cells.
 *
 * The source's floating PTSB logo (an inline `cid:` JPEG) is not carried: the
 * `.eml` is a single text/html part and the logo cannot be embedded within the
 * edge WAF's ~8 KB request cap (#710).
 *
 * Every piece of text resolves from the SAME content-catalogue keys as
 * {@link dataBreachNotificationBody} (#637 inc 27) and is HTML-escaped at render;
 * the emphasis is applied to the default wording only (an admin override
 * plain-renders). The body carries no interpolated context.
 */
function dataBreachNotificationBodyHtml(
  content?: Readonly<Record<string, string>>,
): string {
  // The source keeps its double spaces visible (Word emits `&nbsp;` pairs), so
  // a run of two spaces renders as " &nbsp;" rather than collapsing.
  const t = (key: DataBreachNotificationKey): string =>
    escapeHtml(dataBreachNotificationLine(key, content)).replace(
      / {2}/g,
      ' &nbsp;',
    );
  // Applies source emphasis to the (escaped) default wording.
  const em = (
    key: DataBreachNotificationKey,
    from: string,
    to: string,
  ): string => t(key).replace(from, to);
  const nb = '&nbsp;';
  const p = (html: string, style = ''): string =>
    `<p${style ? ` style="${style}"` : ''}>${html}</p>`;
  const span = (style: string, html: string): string =>
    `<span style="${style}">${html}</span>`;
  // Grey 9pt italic guidance paragraph (the right-hand column).
  const hint = (html: string): string =>
    p(`<i>${span('font-size:9.0pt;color:gray', html)}</i>`);
  const white = (html: string): string => `<b>${span('color:white', html)}</b>`;

  const B1 = 'solid windowtext 1.0pt';
  const B15 = 'solid windowtext 1.5pt';
  const OR = 'solid #ED7D31 1.0pt';
  const pad = 'padding:0cm 5.4pt';
  const tableOpen = (style: string, width?: number): string =>
    `<table border="0" cellspacing="0" cellpadding="0"${width ? ` width="${width}"` : ''} style="${style}border-collapse:collapse">`;
  const td = (
    width: number,
    style: string,
    html: string,
    colspan?: number,
  ): string =>
    `<td width="${width}"${colspan ? ` colspan="${colspan}"` : ''} valign="top" style="${style}">${html}</td>`;

  type Col = [number, string, number?]; // [px width, pt width, colspan]
  // A question table body: `[question html, answer html]` rows, outer border
  // 1.5pt (top on the first row when `topRule`, bottom on the last), inner 1pt.
  const qaRows = (
    left: Col,
    right: Col,
    rows: Array<[string, string]>,
    topRule: boolean,
    bottomRule = true,
  ): string =>
    rows
      .map(([q, a], i) => {
        const first = i === 0 && topRule;
        const bottom = bottomRule && i === rows.length - 1 ? B15 : B1;
        const h = ';height:19.15pt';
        return (
          '<tr style="height:19.15pt">' +
          td(
            left[0],
            `width:${left[1]};border-top:${first ? B15 : 'none'};border-left:${B15};border-bottom:${bottom};border-right:${B1};${pad}${h}`,
            q,
          ) +
          td(
            right[0],
            `width:${right[1]};border-top:${first ? B15 : 'none'};border-left:none;border-bottom:${bottom};border-right:${B15};${pad}${h}`,
            a,
            right[2],
          ) +
          '</tr>'
        );
      })
      .join('');
  // A slate Section header row (Sections 2–4): wide cell + narrow cell.
  const slateHeader = (
    wide: Col,
    narrow: Col,
    height: string,
    html: string,
  ): string => {
    const base = `border-top:${OR};border-bottom:${B1};background:#44546A;${pad};height:${height}`;
    return (
      `<tr style="height:${height}">` +
      td(
        wide[0],
        `width:${wide[1]};${base};border-left:${OR};border-right:none`,
        html,
        2,
      ) +
      td(
        narrow[0],
        `width:${narrow[1]};${base};border-left:none;border-right:${OR}`,
        '',
      ) +
      '</tr>'
    );
  };
  const spacer8 = p(
    `<b>${span('font-size:8.0pt', nb)}</b>`,
    'margin-bottom:8.0pt',
  );
  const SEC_TABLE = 'width:752.25pt;margin-left:5.75pt;';
  const LEFT: Col = [275, '206.5pt'];
  const RIGHT: Col = [728, '545.75pt', 2];

  // Section 1 — red header bar, then its own question table.
  const s1Header =
    tableOpen('width:631.7pt;', 842) +
    '<tr style="height:9.85pt">' +
    td(
      659,
      `width:494.5pt;border-top:${OR};border-left:${OR};border-bottom:${B1};border-right:none;background:#C0504D;${pad};height:9.85pt`,
      p(white(em('s1_header', 'for ALL Data', 'for <u>ALL </u>Data'))),
    ) +
    td(
      183,
      `width:137.2pt;border-top:${OR};border-left:none;border-bottom:${B1};border-right:none;background:#C0504D;${pad};height:9.85pt`,
      p(white(nb)),
    ) +
    '</tr></table>';
  // The "****Please also complete ONE…" routing block (9pt italic black, the
  // breach types bold red, the SECTION references underlined).
  const i9 = (html: string, colour = 'black'): string =>
    `<i>${span(`font-size:9.0pt;color:${colour}`, html)}</i>`;
  const route = (
    key: DataBreachNotificationKey,
    phrase: string,
    section: string,
  ): string =>
    p(
      i9(
        t(key)
          .replace(phrase, `<b>${span('color:red', phrase)}</b>`)
          .replace(section, `<u>${section}</u>`),
      ),
    );
  const routing =
    p(
      i9(
        em(
          'complete_one_header',
          'complete ONE of',
          'complete <b><u>ONE </u></b>of',
        ).replace(/^\*\*\*\*/, '<b>****</b>'),
      ),
    ) +
    route('route_section2', 'External, An Post issue', 'SECTION 2') +
    route('route_section3', 'Inaccurate Customer Data', 'SECTION 3') +
    route('route_section4', 'Human Error', 'SECTION 4)') +
    route('route_section5', 'CCR impacting issue', 'SECTION 5') +
    p(`<b>${i9(t('delete_unused_sections'))}</b>`);
  const s1Body =
    tableOpen('margin-left:5.4pt;') +
    qaRows(
      [294, '220.65pt'],
      [713, '534.8pt'],
      [
        [p(t('date_breach_identified')), p(nb)],
        [p(t('late_notification')), p(nb)],
        [p(t('how_became_aware')), hint(t('how_became_aware_hint'))],
        [p(t('complaint_logged')), hint(t('complaint_logged_hint'))],
        [
          p(t('list_data_subjects')),
          hint(t('list_data_subjects_hint')) + hint(nb),
        ],
        [
          p(t('list_account_numbers')),
          hint(
            em(
              'list_account_numbers_hint',
              'List ALL',
              'List <b><u>ALL</u></b>',
            ),
          ),
        ],
        [p(t('vulnerable_or_special')), hint(t('vulnerable_or_special_hint'))],
      ],
      true,
      false,
    ) +
    '<tr style="height:19.15pt">' +
    td(
      1007,
      `width:755.45pt;border:${B15};border-top:none;${pad};height:19.15pt`,
      routing,
      2,
    ) +
    '</tr></table>';

  // Sections 2–4 — slate header bar + question rows in one table.
  const section = (
    narrow: Col,
    wide: Col,
    height: string,
    headerHtml: string,
    rows: Array<[string, string]>,
  ): string =>
    tableOpen(SEC_TABLE, 1003) +
    slateHeader(wide, narrow, height, headerHtml) +
    qaRows(LEFT, RIGHT, rows, false) +
    '</table>';
  const householder = (
    hintKey: DataBreachNotificationKey,
    lettersKey: DataBreachNotificationKey,
  ): string => hint(t(hintKey)) + hint(t(lettersKey));
  const s2 = section(
    [219, '164.3pt'],
    [784, '587.95pt'],
    '9.85pt',
    p(white(t('s2_header'))),
    [
      [p(t('s2_address_accurate')), hint(t('s2_address_accurate_hint'))],
      [
        p(t('s2_correspondence_not_received')),
        hint(t('s2_correspondence_not_received_hint')) +
          hint(t('s2_attach_copy')),
      ],
      [p(t('s2_correspondence_date')), hint(nb)],
      [
        p(t('s2_immediate_action')),
        hint(t('s2_immediate_action_hint')) + hint(nb),
      ],
    ],
  );
  const s3 = section(
    [214, '160.8pt'],
    [789, '591.45pt'],
    '8.9pt',
    p(white(t('s3_header'))),
    [
      [p(t('s3_how_long_moved')), p(`<i>${span('color:gray', nb)}</i>`)],
      [
        p(em('s3_listed_all_accounts', 'listed ALL', 'listed <u>ALL</u>')),
        hint(t('s3_listed_all_accounts_hint')),
      ],
      [p(t('s3_returned_post')), hint(t('s3_returned_post_hint'))],
      [
        p(t('s3_list_all_correspondence').replace(/\bALL\b/g, '<u>ALL</u>')),
        hint(nb),
      ],
      [
        p(t('s3_address_updated')) +
          p(em('s3_address_updated_ifno', 'If NO,', 'If <b>NO</b>,')),
        hint(t('s3_yesno')) + hint(t('s3_ifno_measures')),
      ],
      [
        p(t('s3_secured_restored')) + p(t('s3_secured_restored_ifnot')),
        householder('s3_householder_letter_hint', 's3_template_letters'),
      ],
      [p(t('s3_other_actions')) + p(nb).repeat(4), hint(nb)],
    ],
  );
  const prior = (key: DataBreachNotificationKey): string =>
    p(em(key, 'place prior to', 'place <b>prior</b> to'));
  const now = (key: DataBreachNotificationKey): string =>
    p(em(key, 'you now put in place', 'you <b>now </b>put in place'));
  const detailed = (key: DataBreachNotificationKey): string =>
    p(
      em(key, 'a detailed description', 'a <b><u>detailed</u></b> description'),
    );
  const ifYes = (key: DataBreachNotificationKey): string =>
    p(em(key, 'If YES,', 'If <b>YES</b>,'));
  const s4 = section(
    [214, '160.8pt'],
    [789, '591.45pt'],
    '8.9pt',
    p(
      white(
        em(
          's4_header',
          'you have not submitted',
          'you <u>have not</u> submitted',
        ),
      ),
    ) + p(white(t('s4_ccr_redirect'))),
    [
      [p(t('s4_responsible_unit')), hint(t('s4_responsible_unit_hint'))],
      [detailed('s4_description'), hint(t('s4_description_hint'))],
      [p(t('s4_listed_all')), hint(t('s4_listed_all_hint'))],
      [
        p(em('s4_list_correspondence', 'List ALL', 'List <u>ALL</u>')) + p(nb),
        hint(nb),
      ],
      [
        p(t('s4_secured_restored')),
        householder('s4_householder_letter_hint', 's4_template_letters'),
      ],
      [prior('s4_measures_before'), hint(t('s4_measures_before_hint'))],
      [p(t('s4_deficiencies')), hint(t('s4_deficiencies_hint'))],
      [now('s4_measures_now'), hint(t('s4_measures_now_hint'))],
      [p(t('s4_consequences')), hint(t('s4_consequences_hint'))],
      [
        p(t('s4_customer_aware')) + ifYes('s4_customer_aware_ifyes'),
        hint(t('s4_customer_aware_yesno')) +
          hint(t('s4_customer_aware_ifyes_detail')),
      ],
    ],
  );

  // Section 5 — its own slate header table, then the question table.
  const s5Header =
    tableOpen('width:751.2pt;margin-left:6.7pt;', 1002) +
    '<tr style="height:8.9pt">' +
    td(
      1002,
      `width:751.2pt;border-top:${OR};border-left:${OR};border-bottom:${B1};border-right:none;background:#44546A;${pad};height:8.9pt`,
      p(white(t('s5_header'))),
    ) +
    '</tr></table>';
  const actionItem = (key: DataBreachNotificationKey): string =>
    `<li style="color:gray"><i>${span('font-size:9.0pt', t(key).replace(/^[a-z]\.\t/, ''))}</i></li>`;
  const s5Body =
    tableOpen('margin-left:5.4pt;') +
    qaRows(
      [304, '227.75pt'],
      [699, '524.5pt'],
      [
        [p(t('s5_responsible_unit')), hint(t('s5_responsible_unit_hint'))],
        [
          detailed('s5_description'),
          hint(t('s5_description_hint1')) + hint(t('s5_description_hint2')),
        ],
        [
          p(t('s5_known_cie')) + p(t('s5_single_multiple')),
          hint(t('s5_known_cie_hint')) + hint(nb) + hint(t('s5_consider_more')),
        ],
        [p(t('s5_how_long')), hint(t('s5_how_long_hint'))],
        [p(t('s5_prior_arrears')), hint(t('s5_prior_arrears_hint'))],
        [p(t('s5_account_closed')), hint(t('s5_account_closed_hint'))],
        [
          p(t('s5_actions_taken')),
          '<ol type="a" style="margin-top:0cm;margin-bottom:0cm">' +
            actionItem('s5_action_a') +
            actionItem('s5_action_b') +
            '</ol>',
        ],
        [prior('s5_measures_before'), hint(t('s5_measures_before_hint'))],
        [p(t('s5_deficiencies')), hint(t('s5_deficiencies_hint'))],
        [now('s5_measures_now'), hint(t('s5_measures_now_hint'))],
        [p(t('s5_consequences')), hint(t('s5_consequences_hint'))],
        [
          p(t('s5_customer_aware')) + ifYes('s5_customer_aware_ifyes'),
          hint(t('s5_customer_aware_yesno')) +
            hint(t('s5_customer_aware_ifyes_detail')),
        ],
      ],
      true,
    ) +
    '</table>';

  // Data Protection Team block.
  const dpLabel = (key: DataBreachNotificationKey): string =>
    p(`<b><i><u>${span('font-size:12.0pt', t(key))}</u></i></b>`);
  const dpRow = (key: DataBreachNotificationKey, height: string): string =>
    `<tr style="height:${height}">` +
    td(
      311,
      `width:233.5pt;border:${B1};border-top:none;${pad};height:${height}`,
      dpLabel(key),
    ) +
    td(
      699,
      `width:524.5pt;border:${B1};border-top:none;border-left:none;padding:0cm;height:${height}`,
      p(span('font-size:12.0pt', nb)),
    ) +
    '</tr>';
  const dp =
    tableOpen('width:758.0pt;', 1011) +
    '<tr style="height:21.1pt">' +
    td(
      1011,
      `width:758.0pt;border:${B1};background:#C0504D;${pad};height:21.1pt`,
      p(white(t('dp_team'))),
      2,
    ) +
    '</tr>' +
    dpRow('dp_reference', '21.1pt') +
    dpRow('comments', '42.5pt') +
    '</table>';

  return [
    '<!DOCTYPE html>',
    '<html>',
    '<head>',
    `<style>p,li{margin:0cm;font-size:11.0pt;font-family:'Calibri',sans-serif;}ol{margin-bottom:0cm;}</style>`,
    '</head>',
    '<body lang="EN-IE" link="#0563C1" vlink="#954F72">',
    p(nb),
    p(
      `<b>${span('font-size:24.0pt;color:#70AD47;letter-spacing:.25pt', t('heading'))}</b>`,
      'margin-right:113.35pt',
    ),
    s1Header,
    s1Body,
    spacer8,
    s2,
    p(span("font-size:12.0pt;font-family:'Arial',sans-serif", nb)),
    s3,
    spacer8,
    s4,
    spacer8,
    s5Header,
    s5Body,
    p(span("font-size:12.0pt;font-family:'Times New Roman',serif", nb)),
    dp,
    p(nb),
    '</body>',
    '</html>',
  ].join('\n');
}

/**
 * "Everyday Lending Data Breach" — the product-specific Data Breach Notification
 * draft for an Everyday Lending correspondence breach. The subject carries the
 * source template's `EDL:` prefix and prefills the in-context customer CIF (in
 * place of the source `XXXXX` placeholder, like {@link EDS_DATA_BREACH}); the
 * body is the shared five-section notification template (see
 * {@link DATA_BREACH_NOTIFICATION_BODY}), reproduced verbatim. No fixed `to:` —
 * the agent addresses the completed draft to the Data Protection Team per the
 * BAU data-breach process.
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` (the shared {@link DATA_BREACH_NOTIFICATION_BODY_HTML}); the
 * plain-text `build` is retained for the subject and as the `mailto:` fallback.
 * Source: Drive "SCRN 23, 24 & 39 / EVERYDAY LENDING Data Breach.msg".
 *
 * #637 inc 27: the shared body + HTML now resolve their editable wording from the
 * content catalogue; the subject prefix + in-context CIF stay fixed in code.
 */
export const EVERYDAY_LENDING_DATA_BREACH: EmailTemplate = {
  id: 'everyday-lending-data-breach',
  label: 'Everyday Lending Data Breach',
  // #842 — addressed to the Sales admin mailbox, CC'ing the agent's team leader
  // (a literal placeholder in the source draft, no fixed SMTP address), per the
  // source Outlook draft (@dylan219 confirmed).
  to: 'SalesAdmin@PTSB.ie',
  cc: '**YOUR TEAM LEADER**',
  build: ({ cif = '', content }) => ({
    subject: `EDL: Data Breach - CIF: ${cif || 'XXXXX'}`,
    body: dataBreachNotificationBody(content),
  }),
  buildHtml: ({ content }) => dataBreachNotificationBodyHtml(content),
};

/**
 * "Business Banking Data Breach" — the product-specific Data Breach Notification
 * draft for a Business Banking correspondence breach. Subject carries the source
 * template's `BB:` prefix and prefills the in-context CIF; the body is the shared
 * notification template ({@link DATA_BREACH_NOTIFICATION_BODY}), verbatim. No
 * fixed `to:` — addressed to the Data Protection Team by the agent.
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` (the shared {@link DATA_BREACH_NOTIFICATION_BODY_HTML}); the
 * plain-text `build` is retained for the subject and as the `mailto:` fallback.
 * Source: Drive "SCRN 23, 24 & 39 / BUSINESS BANKING Data Breach.msg".
 *
 * #637 inc 27: the shared body + HTML now resolve their editable wording from the
 * content catalogue; the subject prefix + in-context CIF stay fixed in code.
 */
export const BUSINESS_BANKING_DATA_BREACH: EmailTemplate = {
  id: 'business-banking-data-breach',
  label: 'Business Banking Data Breach',
  // #842 — addressed to the Sales admin mailbox, CC'ing the agent's team leader
  // (literal placeholder; no fixed SMTP address), per the source draft.
  to: 'SalesAdmin@PTSB.ie',
  cc: '**YOUR TEAM LEADER**',
  build: ({ cif = '', content }) => ({
    subject: `BB: Data Breach - CIF: ${cif || 'XXXXX'}`,
    body: dataBreachNotificationBody(content),
  }),
  buildHtml: ({ content }) => dataBreachNotificationBodyHtml(content),
};

/**
 * "Mortgage Data Breach" — the product-specific Data Breach Notification draft
 * for a Mortgage correspondence breach. Subject carries the source template's
 * `MTG:` prefix and prefills the in-context CIF; the body is the shared
 * notification template ({@link DATA_BREACH_NOTIFICATION_BODY}), verbatim. No
 * fixed `to:` — addressed to the Data Protection Team by the agent.
 *
 * Delivered as a formatted downloadable Outlook draft (`.eml`, #434) via
 * `buildHtml` (the shared {@link DATA_BREACH_NOTIFICATION_BODY_HTML}); the
 * plain-text `build` is retained for the subject and as the `mailto:` fallback.
 * Source: Drive "SCRN 23, 24 & 39 / MORTGAGE Data Breach.msg".
 *
 * #637 inc 27: the shared body + HTML now resolve their editable wording from the
 * content catalogue; the subject prefix + in-context CIF stay fixed in code.
 */
export const MORTGAGE_DATA_BREACH: EmailTemplate = {
  id: 'mortgage-data-breach',
  label: 'Mortgage Data Breach',
  // #842 — addressed to the Sales admin mailbox, CC'ing the agent's team leader
  // (literal placeholder; no fixed SMTP address), per the source draft.
  to: 'SalesAdmin@PTSB.ie',
  cc: '**YOUR TEAM LEADER**',
  build: ({ cif = '', content }) => ({
    subject: `MTG: Data Breach - CIF: ${cif || 'XXXXX'}`,
    body: dataBreachNotificationBody(content),
  }),
  buildHtml: ({ content }) => dataBreachNotificationBodyHtml(content),
};

/** Templates surfaced on the Card Maintenance screen (SCRN-49/50). */
export const CARD_MAINTENANCE_TEMPLATES: readonly EmailTemplate[] = [
  GAMBLING_BLOCK,
  TRAVEL_SPEND,
  APPLE_GOOGLE_PAY_TOKEN,
  EDS_DATA_BREACH,
  BUSINESS_BANKING_DATA_BREACH,
];

/** Templates surfaced on the Change Customer Details screen (SCRN-24). */
export const PERSONAL_DETAILS_TEMPLATES: readonly EmailTemplate[] = [
  EDS_DATA_BREACH,
  EVERYDAY_LENDING_DATA_BREACH,
  BUSINESS_BANKING_DATA_BREACH,
  MORTGAGE_DATA_BREACH,
];

/** Templates surfaced on the Statements screen (SCRN-38/39/40) — #704. */
export const STATEMENTS_TEMPLATES: readonly EmailTemplate[] = [EDS_DATA_BREACH];

/** Templates surfaced on the Postal Address screen (SCRN-23) — #759. */
export const POSTAL_ADDRESS_TEMPLATES: readonly EmailTemplate[] = [
  EDS_DATA_BREACH,
];
